/**
 * JeanieIQ left sidebar — fixed-width nav rail rendered on every page.
 *
 * Markup mirrors the demo's `.sb` block
 * (the platform sidebar source of truth — every section's demo copies this
 * block verbatim).
 * The structure (logo → tenant card → guidance engine → grouped nav items)
 * is the same on every section of the platform; only the active item
 * changes with the route.
 *
 * All labels go through i18n (`common:sidebar.*`). The list of nav items
 * is hard-coded here for now — every section of the platform will be a
 * `<Link>` once its page exists, and routes that are not yet built point
 * to a stub page (handled by react-router in App.tsx).
 *
 * Icon set is intentionally inline SVG copied from the demo so we don't
 * need to pull in an icon library for Phase 1.
 */

import { NavLink, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchSupplierBadge } from '../lib/approvedSuppliersApi';
import { fetchHolidayBadge } from '../lib/holidayApi';
import { fetchEnquiryBadge } from '../lib/standardSalesApi';
import { fetchOnboardingBadge } from '../lib/salesApi';
import { fetchBuildingMaintenanceBadge } from '../lib/buildingMaintenanceApi';
import { fetchInformationSecurityBadge } from '../lib/informationSecurityApi';
import {
  fetchExpiringCount as fetchAccreditationsExpiringCount,
  fetchAchieveSubscriptions,
} from '../lib/accreditationsApi';
import { fetchFleetExpiringCount } from '../lib/fleetApi';
// The show/deny rule, shared with useHasSection and mirroring the API's onShowList.
import { tenantHoldsSection } from '../lib/tenantSections';
import { tenantPlace } from '../lib/tenantPlace';
import { fetchOverdueCount as fetchPlantOverdueCount } from '../lib/plantEquipmentApi';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useCurrentTenant } from '../lib/useCurrentTenant';
import { pageOfferedTo } from '../pages/pharmacyHub/categories';
import { getCurrentUserRole } from '../lib/auth';
import { useSectionTiers } from '../lib/useSectionTiers';
import { publishDrawnNav } from '../lib/drawnNav';
// 2026-06-10 — Phase 2 per-user section permissions: the signed-in user's
// denied sidebar sections come from /api/auth/me.
import { fetchMe } from '../lib/meApi';
import { ISO_STANDARDS, type AccreditationStandard, type Nation } from '@jeanieiq/types';
// The icon set lives in its own file, shared with the Platform Admin sidebar.
import { Icon } from './navIcons';
import { useBranch } from '../lib/branchContext';
import { moduleVisibleInNation, moduleLabelKey } from '../lib/moduleNations';
import type { TenantProfile } from '../lib/tenantApi';
import { CATALOGUED_BUT_OWN_SCREEN, CATEGORIES } from '../pages/pharmacyHub/categories';
import { CARE_HOME_PATHS } from '../pages/pharmacyHub/careHomeModules';

/**
 * 2026-05-13 — tenant feature flag keys that gate sidebar visibility.
 * The boolean fields on TenantProfile that hide / show a nav entry
 * outright. Keep this in sync with TenantProfile when adding new
 * add-on modules.
 */
type TenantFeatureFlag =
  | 'payrollEnabled'
  | 'subContractEnabled'
  | 'cashflowEnabled'
  | 'auditPackEnabled'
  /** Finance -> Tronc paid add-on (2026-08-16, TRONC-UNGATED-001). */
  | 'troncEnabled';

export interface NavItem {
  to: string;
  /**
   * 2026-06-19 — if set, the item shows as active whenever the current path
   * starts with this prefix (not only when it matches `to`). Needed for the
   * Admin hub (to=/administration) to stay highlighted on its /admin/* sub-
   * pages (Subscription, Users, Document Register, Kiosks). '/administration'
   * itself also starts with '/admin', so one prefix covers most of it.
   *
   * 2026-09-18 — a LIST, because the admin section is NOT all under /admin.
   * The Compliance Gate is an Admin hub card (AdministrationPage line 154)
   * and lives at /gate, so the single "/admin" prefix missed it and nothing
   * in the sidebar lit up when a notification took you there.
   */
  activePrefix?: string | string[];
  /**
   * 2026-09-29 — paths this row owns OUTRIGHT, as well as its own `to`.
   *
   * Unlike `activePrefix` this is read by navEntryActive, so it works on a
   * sub-row; and it matches a whole path segment (`/pmr` does not claim
   * `/pmr-import`) rather than any string prefix.
   *
   * Pharmacy's "Dispensary Management" is the only user: the four bank tabs
   * navigate away to forty-odd module routes that all still render inside the
   * same shell, so without this the sidebar went dark the moment you used the
   * section you had just opened.
   *
   * It yields to any row pointing exactly at the current path, so giving one
   * of those modules its own sidebar row later needs no change here.
   */
  activeWhenIn?: readonly string[];
  /**
   * 2026-08-25 — other `?tab=` values on the SAME path that should also light
   * this entry.
   *
   * Stock's Summary page carries three tabs of its own (Summary, Parts
   * Catalogue, Stock Movements), and only Summary is a sidebar row. Without
   * this, clicking one of the other two left NO sidebar entry matching, so the
   * highlight vanished while you were still on the page it points at. Reagan:
   * *"the sidebar unhighlights off sumary. it needs to stay highlighted on
   * summary as your still on that page"*.
   *
   * Deliberately a list on the entry rather than a rule in the matcher: it says
   * WHICH tabs belong to this row, so the next person can see the relationship
   * instead of inferring it.
   */
  activeTabs?: ReadonlyArray<string>;
  /**
   * 2026-08-25 — claim any `?tab=` on this path that NO sidebar row pins and no
   * sibling lists in its own `activeTabs`.
   *
   * For in-page tabs whose ids are DATA, not code. Stock's brand catalogues are
   * per-tenant rows from the database, so their ids cannot be written into
   * `activeTabs` — a tenant adds a brand and the list is silently out of date.
   * Stating what is NOT a row is possible and stays correct: every function view
   * of Stock has its own row, so the leftovers are exactly the brands.
   *
   * Narrow on purpose. It yields to any row that pins the tab and to any
   * sibling's `activeTabs`, so giving one of those tabs its own row later needs
   * no change here — the same "most specific wins" rule as the rest of this
   * matcher.
   */
  claimsUnpinnedTabs?: boolean;
  /** Translation key under `common:sidebar.nav` */
  labelKey: string;
  /** The words only, when they differ from `labelKey`; `labelKey` stays the identity (permissions, keys). */
  displayLabelKey?: string;
  icon: ReactNode;
  /** Optional small badge (e.g. `LIVE` for the compliance gate). */
  badge?: { text: string; variant: 'gold' | 'blue' };
  /**
   * Stage 1.2e (2026-05-10) — if set, the item only renders for users
   * whose role is in this list. Used to gate admin-only entries
   * (Kiosks, Roles, etc.). Undefined = visible to every authenticated
   * user (the default).
   */
  roles?: ReadonlyArray<string>;
  /**
   * 2026-05-13 — if set, the item only renders when the tenant has the
   * matching boolean flag enabled. Used for paid add-on modules in the
   * Finance group (Payroll / Sub Contract / Cashflow / Audit Pack). The
   * route still exists; the sidebar just doesn't surface a link to it.
   */
  tenantFlag?: TenantFeatureFlag;
  /** Shown only in the company named as the Operator in Platform Admin > Settings. */
  operatorOnly?: boolean;
  /**
   * 2026-06-22 — if set, this item is a collapsible PARENT: it renders as a
   * toggle (no route of its own) and its children drop down beneath it in the
   * same sidebar. Used for the Pharmacy section — one entry, its modules nested.
   */
  children?: readonly NavItem[];
}

/**
 * Sidebar count badge — used for live counts (overdue, pending, expiring).
 * Single source of truth for the badge variants; the colour is driven by
 * the `nb-{red|amber|blue}` CSS classes defined in theme.css so the JSX
 * stays declarative and there are no inline-style colours.
 */
type BadgeVariant = 'red' | 'amber' | 'blue';
const BADGE_CLASS: Record<BadgeVariant, string> = {
  red: 'nb-r',
  amber: 'nb-a',
  blue: 'nb-b',
};
/**
 * HOW BIG IS THE SALES BADGE, and on which row (2026-10-14).
 *
 * Pulled out as a rule rather than left inline because it is the one part
 * of this that can be wrong in a way nobody would notice: a number is a
 * number, and a parent quietly showing only half of what is waiting looks
 * exactly like a parent showing all of it.
 *
 * THE PARENT SUMS BOTH. A child badge is only visible once the section is
 * open, and the section is only open when you are already inside it — so a
 * count that lived solely on the Onboarding tab would announce a waiting
 * customer to somebody who had already gone looking. That is the problem
 * Reagan reported, not the fix for it.
 *
 * A SUM OF TWO DIFFERENT THINGS, deliberately. The parent answers one
 * question — "is there anything in Sales needing me?" — and an unassigned
 * enquiry and a customer waiting for their login both say yes. WHICH it is,
 * is the child badges’ job.
 *
 * Returns null rather than 0, because the caller renders nothing for null
 * and a literal "0" on a sidebar row is worse than no badge at all.
 */
export function salesBadgeCount(
  labelKey: string,
  counts: {
    /** Unassigned enquiries BELONGING TO SALES. */
    unassignedSales: number;
    /**
     * Unassigned enquiries across BOTH sections — what the Standard Sales &
     * Jobs row has always counted, and still does.
     *
     * Reagan, 2026-10-15: *"leave the standard sales and jobs the way they
     * were without us touching them. i want only the front desk and sales bit
     * to work for us on this branch."* So the two rows are deliberately
     * asymmetric: Sales counts its own, the jobs row counts the lot. The
     * names say which is which, because a reader would otherwise assume they
     * were a matched pair.
     */
    unassignedAnySection: number;
    awaitingOnboarding: number;
  },
): number | null {
  const n =
    labelKey === 'sales'
      ? counts.unassignedSales + counts.awaitingOnboarding
      : labelKey === 'sales_onboarding'
        ? counts.awaitingOnboarding
        : labelKey === 'standard_sales'
          ? counts.unassignedAnySection
          : 0;
  return n > 0 ? n : null;
}

function CountBadge({
  count,
  variant,
  testId,
}: {
  count: number;
  variant: BadgeVariant;
  testId: string;
}): JSX.Element {
  return (
    <span className={BADGE_CLASS[variant]} data-testid={testId}>
      {count}
    </span>
  );
}

export interface NavGroup {
  /** Translation key under `common:sidebar.groups`, or null for the top group. */
  titleKey: string | null;
  items: NavItem[];
}

// Six sidebar groups: Dashboard ungrouped at top, then People & HR,
// Operations, Sales & Stock, Finance, Legal & Compliance, Resources.
// Contracts & Legal is the only item in Legal & Compliance —
// it routes to a stub until its own session ships.
/**
 * Does this tenant have this section? THE rule - the point where a Platform
 * Admin selection becomes what a customer can open.
 *
 * Shane, 2026-08-22: *"the admin library feeds the tennent sector, that feeds
 * the configuration, that feeds the tennant platform"*. This is the last link.
 *
 *   selectedSections has entries  -> it IS the list (a SHOW list). Anything
 *                                    not on it is not shown, so a section
 *                                    added to the platform next month appears
 *                                    for nobody until somebody decides.
 *   selectedSections is empty     -> nobody has configured this tenant yet;
 *                                    fall back to hiddenSections (the old DENY
 *                                    list) and behave exactly as before.
 *
 * The fallback is what made the changeover safe to land: of 77 tenants only a
 * handful carry any configuration, and the rest keep working untouched rather
 * than going dark on the day the rule changed. It retires once every tenant
 * has a selection - Configuration's "Adopt" converts one in a click, deriving
 * the show list from the deny list so their screen does not change.
 *
 * Exported and pure so it can be tested on its own. It decides what a paying
 * customer can reach; that is not a thing to leave buried in a render.
 *
 * THE BODY MOVED to lib/tenantSections.ts on 2026-09-30, unchanged. The same
 * question was being answered in three places and `useHasSection` was getting
 * it wrong for an unconfigured tenant — drawing a section in this sidebar
 * while a button inside that page decided the tenant did not hold it. One
 * rule, one home. This stays exported because Sidebar.sections.test.tsx tests
 * it here, and that is still the right place to assert what a customer can
 * reach.
 */
export function tenantHasSection(
  labelKey: string,
  selectedSections: readonly string[],
  hiddenSections: readonly string[],
): boolean {
  return tenantHoldsSection(labelKey, selectedSections, hiddenSections);
}

/**
 * Which of the three Rota children a tenant sees.
 *
 * Two independent rules, both right in production:
 *   • a pharmacy tenant rosters on the dedicated Pharmacy Rota Builder, so the
 *     GENERIC Shift Builder is hidden to avoid two of them. Timesheets and
 *     Shift Management are untouched.
 *   • `shiftManagementEnabled === false` hides Shift Management, for tenants
 *     on fixed hours who do not do shift work.
 *
 * And one override on top - "OVERRIDE ALL SHIFT patterns on" (2026-08-30).
 * Shane: *"he cannot stop everyone from seeing things in testing"*. Both rules
 * live where a tenant cannot reach them, so a tester signed in as that tenant
 * had no way to see a screen it was gated out of. When the platform switch is
 * on, both are bypassed. It reveals menu entries and nothing else: no route,
 * no section grant, no change to what the tenant has bought.
 *
 * Exported and pure so all four combinations can be tested, including OFF -
 * proving the override changes nothing until somebody ticks it.
 */
/** What a sub-tab is called for this tenant: a pharmacy's builder is its Rota Builder. */
export function subTabLabelKey(labelKey: string, isPharmacy: boolean): string {
  return isPharmacy && labelKey === 'rota_builder' ? 'pharmacy_rota' : labelKey;
}

/**
 * HR's children for this tenant: GPhC registration and NHS smartcards are a
 * pharmacy's registers and nobody else's.
 *
 * THE SECTION, NOT THE SECTOR, for the same reason rotaChildrenFor says so
 * below: /api/gphc-smartcards answers whoever the section was given to, and a
 * sidebar that disagrees with the API hands out a link that can only 403.
 */
export function hrChildrenFor(
  children: readonly NavItem[],
  opts: { isPharmacy: boolean },
): readonly NavItem[] {
  if (opts.isPharmacy) return children;
  return children.filter((c) => c.labelKey !== 'hr_gphc');
}

export function rotaChildrenFor(
  children: readonly NavItem[],
  opts: {
    isPharmacy: boolean;
    shiftManagementEnabled: boolean;
    overrideAllShiftPatterns: boolean;
  },
): readonly NavItem[] {
  if (opts.overrideAllShiftPatterns) return children;
  return children
    .filter((c) => {
      if (c.to === '/rota?tab=shifts' && !opts.shiftManagementEnabled) return false;
      /*
       * SHIFT MANAGEMENT IS NOT FOR A PHARMACY (Reagan, 2026-09-30: "for a
       * pharmacy tennant aswell the shift management does not need to be
       * seen").
       *
       * The generic tab manages shift PATTERNS — the recurring templates a
       * fixed-rota business builds a week from. A pharmacy does not roster
       * that way: its week is built against the branch's staffing
       * requirement, a legal floor that changes by weekday and is measured
       * per day on the Rota Builder. Two ways to shape a week, one of which
       * cannot express the rule the other enforces, is worse than one.
       *
       * Timesheets STAYS. Hours worked are hours worked, and payroll reads
       * them the same whichever builder produced the shift.
       */
      if (c.to === '/rota?tab=shifts' && opts.isPharmacy) return false;
      return true;
    })
    /*
     * A PHARMACY TENANT'S ROTA BUILDER *IS* THE PHARMACY ONE (Reagan,
     * 2026-09-30: "for a pharmacy tennant it needs to be the shift builder").
     *
     * This used to REMOVE the entry, on the reasoning that a pharmacy rosters
     * on the dedicated Pharmacy Rota Builder and two builders in one sidebar
     * is one too many. That was right about the two builders and wrong about
     * what to do: on 2026-09-25 the separate /pharmacy-rota shortcut was taken
     * out as well, to be folded into Shifts & Timesheets later. The folding was
     * never done, so from that day a pharmacy tenant had NEITHER builder — the
     * generic one hidden here, the pharmacy one unlinked there — and the
     * feature came back months later as a request to build what already
     * existed.
     *
     * Pointing the same entry at the pharmacy page is the folding. One Rota
     * Builder in the place everybody looks for it, showing the right one.
     */
    .map((c) => (
      c.to === '/rota?tab=rota' && opts.isPharmacy
        ? { ...c, to: '/pharmacy-rota', displayLabelKey: subTabLabelKey(c.labelKey, true) }
        : c
    ));
}

/** A sub-tab's own rules: its `roles`, and `operatorOnly` for the Operator company's tabs. */
export function childShown(c: NavItem, who: { role: string | null; isOperator: boolean }): boolean {
  if (c.roles && (who.role === null || !c.roles.includes(who.role))) return false;
  if (c.operatorOnly && !who.isOperator) return false;
  return true;
}

/**
 * Which of the ERP & Accounts children a tenant sees.
 *
 * FP34C IS AN ACCOUNTS ENTRY AS WELL AS A PHARMACY MODULE (Kris, 1 Oct 2026).
 * The accountant records what NHSBSA paid against a claim and corrects it, and
 * reaches the claims through the Accounts permission, never through Pharmacy:
 * holding Pharmacy would hand them every clinical register. The API lets
 * either key in (FP34C_SECTIONS in app.ts, the same shape as LOCUM_SECTIONS).
 * The web route has no section gate of its own, only the nation one, so the
 * route already took an accountant; what was missing was a way there. Every
 * link to /fp34c sat inside Pharmacy - the hub tile - which an accounts-only
 * user never sees.
 *
 * It shows only where the monthly claim means something: the tenant runs
 * Pharmacy. A construction tenant's accounts are not offered an NHS claim.
 * Every other child is untouched.
 *
 * BOTH NATIONS since 4 Oct 2026 (TODO 1606). It was England's branches only,
 * while the claim was. The nation test below is kept, and answers yes for
 * both now that `fp34c` is out of MODULE_NATIONS: if the module is ever
 * devolved again this child follows it without being touched. A Welsh branch
 * sees it under its Welsh name (`moduleLabelKey`), like the hub tile.
 *
 * Exported and pure, like rotaChildrenFor and accreditationChildrenFor, so
 * each combination can be tested.
 */
export function accountsChildrenFor(
  children: readonly NavItem[],
  opts: { runsPharmacy: boolean; nation: Nation },
): readonly NavItem[] {
  /*
   * A Motor Trade branch here was added and removed on 2026-10-13 — the
   * dealer's reports are a SUB-TAB of Reporting and need no row of their own.
   * See the nav entry for why. The sub-tab itself is still gated, inside the
   * Accounts page, by REPORTING_SUBTAB_SECTION.
   */
  return children.filter(
    (c) => c.to !== '/fp34c' || (opts.runsPharmacy && moduleVisibleInNation('fp34c', opts.nation)),
  );
}

/**
 * Which of the Accreditations children a tenant sees.
 *
 * FORS and ISO are shown only where the tenant is ACCREDITED to something in
 * that scheme — `tenants.active_standards`, ticked per tenant in Platform Admin
 * -> Configuration. Shane, 2026-09-21: *"the gating needs to be in this area ...
 * as it will be tennent specific not sector"*.
 *
 * Before this, neither was gated at all: both showed for every tenant whatever
 * it held. The only per-tenant answer that existed was the SECTOR's
 * `default_standards`, and all it decided was which ISO boards appeared INSIDE
 * the area — so with every sector carrying an empty list, the ISO area showed
 * no boards while its nav entry stayed visible to everybody.
 *
 * THE REGISTER IS NOT GATED. It lists the contractor schemes (CHAS,
 * SafeContractor and the rest), which a tenant tracks whether or not it holds a
 * certificate here — gating it would hide the one screen that says what they
 * are working toward.
 *
 * The Auditor Portal keeps its own rule, an active Achieve subscription, which
 * is a separate question from what they are certified to: one is what they have
 * bought from us, the other is what a certifying body has awarded them.
 *
 * Exported and pure so every combination can be tested, including the empty
 * one — a tenant that holds nothing must keep the Register and lose both.
 */
export function accreditationChildrenFor(
  children: readonly NavItem[],
  held: readonly string[],
): readonly NavItem[] {
  const holds = new Set(held);
  return children
    .filter((c) => {
      const standards = NAV_TO_STANDARD[c.to];
      // Not a standard's entry (the Register, the Auditor Portal) — untouched.
      if (!standards) return true;
      return standards.some((s) => holds.has(s));
    })
    // One level down too: an ISO area can belong to one standard (Aspects is 14001).
    .map((c) => (c.children ? { ...c, children: accreditationChildrenFor(c.children, held) } : c));
}

/**
 * What clicking a NESTED parent does — collapse, expand, or follow the link.
 *
 * Shane, 2026-09-21: *"can you make the tabs collaps when clicked as well as
 * open"*. A branch opened when the route was inside it and could not be closed
 * again, so a long one stayed open underneath whatever you were reading.
 *
 * Only 'navigate' lets the link go. The other two are a person BROWSING the
 * sidebar rather than asking to move, and a page that jumps under them while
 * they look for something is what the top-level accordion rule was written to
 * stop on 2026-06-28.
 *
 *   routeOpen  the route is on this branch or inside it
 *   collapsed  the person has closed it by hand since they last navigated
 *
 * Exported and pure because this is the whole behaviour: the click handler is
 * three lines around it, and the sidebar has no render harness to test through.
 */
export function branchClickAction(opts: {
  routeOpen: boolean;
  collapsed: boolean;
}): 'collapse' | 'expand' | 'navigate' {
  if (!opts.routeOpen) return 'navigate';
  return opts.collapsed ? 'expand' : 'collapse';
}

/**
 * The thirteen ISO 9001 areas, as the sidebar's dropdown under ISO 9001.
 * Risks & Opportunities (clause 6.1) joined on 2026-10-02, between the Legal
 * Register (4.1) and Objectives (6.2).
 *
 * They were a tab strip on the page (`acc-iso-subtabs`) until 2026-09-21, when
 * Shane moved them here — *"these tabs need to be a dropdown under ISO9001"* —
 * and the strip came off the page in the same change, so there is one way to
 * reach them rather than two that can disagree about which is open.
 *
 * ORDER IS THE CLAUSE ORDER the page already used (Shane, 2026-08-24: *"if the
 * top tabs can be place in order of requirements and clauses"*): Evidence
 * first because it is the store everything lands in, Business & QMS second
 * because nothing can be written until the company describes itself, then
 * clauses 4 through 10, then the two that are about an audit rather than a
 * clause.
 *
 * Addresses are unchanged — `?tab=iso&isoSub=<key>`, addressable since
 * 2026-08-31 — so every deep link already built in the platform, including the
 * non-conformance links from supplier reviews and customer complaints, still
 * lands exactly where it did.
 *
 * No emoji here, unlike the page's strip: the sidebar is plain text throughout
 * and a row of icons in one section only would read as a different kind of
 * thing.
 */
/**
 * THE CARE-HOME SCREENS, AS THEIR OWN SECTION (Reagan, 2026-09-25).
 *
 * These nine were the hub's 'Care Homes' bank. The sidebar link into that hub
 * is labelled "Dispensary Management", and care-home work is not dispensary
 * management — so a pharmacist supplying homes had to already know their
 * screens were nested inside something that does not describe them.
 *
 * THIS IS NOW THE ONLY LIST. The bank was removed from categories.ts in the
 * same change rather than left behind: two lists of the same nine screens is
 * how one of them quietly stops matching, and the hub's version had already
 * outlived a hardcoded count for exactly that reason.
 *
 * Order is the order the work happens, which is how the bank had it: the
 * paperwork arrives before anybody opens a cycle, and the audit comes after
 * the homes exist.
 *
 * No emoji, matching the rest of the sidebar — the hub's tiles carry icons
 * because they are tiles.
 */
/*
 * CARE_HOME_MODULES MOVED (Reagan, 2026-09-29) to
 * pages/pharmacyHub/careHomeModules.ts, where the in-page strip reads it too.
 *
 * It listed nine screens here as nested sidebar rows. The strip now draws those
 * same nine down the left of every care-home page — so the sidebar listing them
 * as well put the same nine names on one screen twice, three inches apart,
 * which is the exact duplication that took the hub's banks out of the sidebar
 * the day before. One row here; the nine live in the strip.
 */
/**
 * Drop the modules this branch's nation does not have, at every depth.
 *
 * Exported and pure so both nations can be tested, in the same spirit as
 * `accreditationChildrenFor` and `rotaChildrenFor` above — a filter observed
 * working in one nation on one screen has been observed in half the cases that
 * matter.
 *
 * IT RECURSES, and has to. `visibleNavGroups` filters the TOP level only, which
 * was sufficient while every devolved module was its own section. Pharmacy's
 * banks became nested groups on 2026-09-28 and six clinical modules are
 * devolved — Pharmacy First, its triage, PCS, NMS and Hypertension are England;
 * PIPs and the Welsh DMR are Wales. As children they would have been offered to
 * every branch in both nations, with nothing on screen looking wrong: a Welsh
 * pharmacist would simply have clicked "Pharmacy First" and been refused by the
 * API's own nationRegistry, in production.
 */
export function navChildrenForNation(
  items: readonly NavItem[],
  nation: Nation,
): readonly NavItem[] {
  return items
    .filter((c) => moduleVisibleInNation(c.labelKey, nation))
    .map((c) => (c.children ? { ...c, children: navChildrenForNation(c.children, nation) } : c));
}

const ISO_9001_AREAS: readonly NavItem[] = [
  { to: '/accreditations?tab=iso&isoSub=evidence', labelKey: 'iso_evidence', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=qmsprofile', labelKey: 'iso_qmsprofile', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=context', labelKey: 'iso_context', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=legalregister', labelKey: 'iso_legalregister', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=risks', labelKey: 'iso_risks', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=aspects', labelKey: 'iso_aspects', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=objectives', labelKey: 'iso_objectives', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=changes', labelKey: 'iso_changes', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=communication', labelKey: 'iso_communication', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=environment', labelKey: 'iso_environment', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=emergency', labelKey: 'iso_emergency', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=feedback', labelKey: 'iso_feedback', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=internalaudit', labelKey: 'iso_internalaudit', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=mgmtreview', labelKey: 'iso_mgmtreview', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=capa', labelKey: 'iso_capa', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=auditreview', labelKey: 'iso_auditreview', icon: Icon.accred },
  { to: '/accreditations?tab=iso&isoSub=achieve', labelKey: 'iso_achieve', icon: Icon.accred },
];

/**
 * Which standard each Accreditations entry belongs to.
 *
 * A map rather than string matching inside the filter: the addresses appear in
 * exactly two places, here and in NAV_GROUPS, and a typo in either shows up as
 * an entry that never appears however it is ticked — which reads as the gate
 * being broken rather than as a typo. The nav-vocabulary test walks both.
 */
// Which standards draw each entry. The ISO area is one area for every ISO standard held.
const NAV_TO_STANDARD: Record<string, readonly AccreditationStandard[]> = {
  '/accreditations?tab=fors&grade=bronze': ['fors_bronze'],
  '/accreditations?tab=fors&grade=silver': ['fors_silver'],
  '/accreditations?tab=fors&grade=gold': ['fors_gold'],
  '/accreditations?tab=iso': ISO_STANDARDS,
  '/accreditations?tab=iso&isoSub=aspects': ['iso_14001'],
  '/accreditations?tab=iso&isoSub=environment': ['iso_14001'],
  '/accreditations?tab=iso&isoSub=emergency': ['iso_14001'],
};

const NAV_GROUPS: NavGroup[] = [
  // Stage 1.2e (2026-05-10) — Administration group. Mixed visibility:
  // My Profile is for every authenticated user (kiosk PIN, Check In);
  // Dashboard + Kiosks are gated to director / manager / admin per
  // Shane (2026-05-10) — those are admin-oriented overviews not
  // useful to operatives, so we hide them from their sidebar. My
  // Profile sits here too to keep all "personal / setup" entries
  // tucked together rather than dangling at the top of the nav.
  {
    titleKey: 'administration',
    items: [
      // 2026-06-03 — Administration hub entry. Per Shane: a normal
      // sidebar item labelled "Admin", not a clickable group header
      // and not reusing the group-title "Administration" text. The
      // existing per-area items (Kiosks, Subscription, Document
      // Register, etc.) stay as direct shortcuts below; this entry
      // is the consolidated card-grid view.
      {
        to: '/administration',
        // Stay highlighted across the whole admin section, incl. the /admin/*
        // sub-pages reached from the hub cards (Subscription, Users, Document
        // Register, Kiosks). '/administration' also starts with '/admin'.
        /*
         * The Admin hub cards that do NOT live under /admin, and so need
         * naming here for the section to light up when you are on them.
         *
         * /gate — Reagan: "the compliance gate is in the admin section so the
         * admin bar needs to light up if its in that section".
         * /my-profile — same again, the My Profile card.
         *
         * Both are cards on AdministrationPage. If a third is added there and
         * does not sit under /admin, it belongs in this list too — that is the
         * one thing this arrangement still asks somebody to remember.
         */
        activePrefix: ['/admin', '/gate', '/my-profile'],
        labelKey: 'admin_hub',
        icon: Icon.dashboard,
        roles: ['director', 'manager', 'admin'],
      },
      // 2026-06-03 — My Profile removed from the sidebar per Shane.
      // Accessible via the Admin hub at /administration. Route
      // /my-profile is unchanged.
      {
        to: '/dashboard',
        labelKey: 'dashboard',
        icon: Icon.dashboard,
        roles: ['director', 'manager', 'admin'],
        /*
         * The company calendar is opened from the dashboard and has no row of
         * its own, so the sidebar went dark the moment you used the page you
         * had just opened. Reagan: *"if your on calander the dashbaord should
         * be lit up still on the side"*.
         *
         * activePrefix and not activeWhenIn: a TOP-LEVEL row is matched by the
         * renderer in this file, which reads the prefix; activeWhenIn is read
         * by navEntryActive, which only the sub-rows go through. It yields to
         * any row pointing exactly at the path, so giving the calendar its own
         * row later needs no change here.
         */
        activePrefix: '/calendar',
      },
      // 2026-06-03 — Kiosks removed from the sidebar (per Shane).
      // Accessible only via the Admin hub at /administration. The
      // /admin/kiosk-setup route itself remains live; the sidebar
      // shortcut is the only thing that's been removed.
      // 2026-06-05 — Document Register removed from the sidebar (per
      // Shane, same treatment as Kiosks / Subscription). Accessible via
      // the Admin hub at /administration (the "Document Register" card →
      // /admin/documents). The /admin/documents route and
      // DocumentRegisterPage are unchanged — this only drops the
      // duplicate sidebar shortcut.
      // 2026-06-03 — Subscription removed from the sidebar (per
      // Shane, same treatment as Kiosks). Accessible only via the
      // Admin hub at /administration. The /admin/subscription route
      // itself remains live.
      // 2026-06-03 — Accreditations moved here from the Operations
      // group per Shane.
      // 2026-06-28 — collapsible parent: the 5 always-on tabs drop down into
      // the sidebar. The Auditor Portal child is appended in the render only
      // when the tenant has an active Achieve subscription (ACCREDITATIONS_
      // AUDITOR_CHILD) — matching the page's conditional tab.
      {
        to: '',
        labelKey: 'accreditations',
        icon: Icon.accred,
        /*
         * 2026-09-21 — the STANDARDS are the entries, not "FORS" and "ISO".
         *
         * Shane, looking at the gated sidebar: *"it is not showing which one is
         * ticked as we asked.. also in the iso what is tehre now is 9001"*. A
         * tenant accredited to ISO 9001 and FORS Silver should read that, not
         * two generic headings that say nothing about what they hold.
         *
         * DECLARED IN FULL HERE AND FILTERED AT RENDER by what the tenant holds
         * (accreditationChildrenFor). Building the list dynamically instead
         * would hide these from everything that walks NAV_GROUPS statically —
         * the breadcrumb trail, the per-user section permission vocabulary —
         * and those would then name a page nobody could reach from a crumb.
         *
         * The FORS grades carry `&grade=` so three entries do not share one
         * address and light up together. The page reads `tab` and ignores it
         * today; it is there for when the grade-specific content is built.
         */
        children: [
          { to: '/accreditations?tab=register', labelKey: 'accreditation_register', icon: Icon.accred },
          { to: '/accreditations?tab=fors&grade=bronze', labelKey: 'accreditation_fors_bronze', icon: Icon.accred },
          { to: '/accreditations?tab=fors&grade=silver', labelKey: 'accreditation_fors_silver', icon: Icon.accred },
          { to: '/accreditations?tab=fors&grade=gold', labelKey: 'accreditation_fors_gold', icon: Icon.accred },
          /*
           * ISO 9001 is a nested parent: its twelve areas moved OUT of the
           * page's sub-tab strip and into the sidebar, at Shane's request —
           * *"these tabs need to be a dropdown under ISO9001"*. Each keeps the
           * `?tab=iso&isoSub=` address it already had, so every deep link built
           * elsewhere in the platform still lands where it did.
           */
          {
            to: '/accreditations?tab=iso',
            labelKey: 'accreditation_iso_9001',
            icon: Icon.accred,
            children: ISO_9001_AREAS,
          },
          /*
           * 2026-08-28 - `?tab=additional` removed. It was a second surface
           * over the same eight contractor schemes the Accreditation Register
           * already lists under "Additional Schemes". Shane: *"the register
           * and Additional acreditations is doing the same thing... that
           * needs to be one folder"*. A saved link to ?tab=additional falls
           * back to the register, because useTabParam validates against an
           * allow-list the value is no longer in.
           */
        ],
      },
      /*
       * Document Register — 2026-08-26, Shane: *"the document list can go in the
       * side bar as a sidebar click that then openes the page with the full
       * list"*.
       *
       * It was moved OUT of the sidebar into the Administration hub on
       * 2026-06-03, at his request, when it was a document library nobody
       * touched daily. Since then it has become the ISO §7.5 controlled-document
       * store: approving a version here files clause evidence, and the register
       * is the list a document footer is drawn from and the first thing an
       * auditor asks to see. Top-level access earns its place now in a way it
       * did not then.
       *
       * A SINGLE LINK, not a collapsible parent — the page carries its own tabs,
       * and duplicating them here is the sprawl the June consolidation removed.
       *
       * 2026-08-26: children were briefly added here, and Shane rejected them on
       * sight — *"the sidebar now caries what the screen caries with tabs?? whast
       * the point"*. He was right, and this comment already said so before I
       * overrode it. "Merge the ISO document register as a child to the document
       * register" meant merge the VIEW into the register page, which is the ISO
       * tab on it; it did not mean copy the tab bar into the nav.
       */
      { to: '/admin/documents', labelKey: 'document_register', icon: Icon.contracts },
      // 2026-06-03 — Contracts & Legal moved here from the dropped
      // "Legal & Compliance" group per Shane.
      // 2026-06-28 — collapsible parent: the 8 tabs drop into the sidebar.
      {
        to: '',
        labelKey: 'contracts_legal',
        icon: Icon.contracts,
        children: [
          { to: '/contracts?tab=library', labelKey: 'contracts_library', icon: Icon.contracts },
          { to: '/contracts?tab=register', labelKey: 'contracts_register', icon: Icon.contracts },
          { to: '/contracts?tab=transportmanager', labelKey: 'contracts_transportmanager', icon: Icon.contracts },
          { to: '/contracts?tab=settings', labelKey: 'contracts_settings', icon: Icon.contracts },
          { to: '/contracts?tab=importexport', labelKey: 'contracts_importexport', icon: Icon.contracts },
        ],
      },
      // The digital asset register, the incident and breach record, and the
      // procedure those two operate.
      {
        to: '',
        labelKey: 'information_security',
        icon: Icon.shield,
        children: [
          { to: '/information-security?tab=assets', labelKey: 'infosec_assets', icon: Icon.shield },
          { to: '/information-security?tab=incidents', labelKey: 'infosec_incidents', icon: Icon.shield },
          { to: '/information-security?tab=access', labelKey: 'infosec_access', icon: Icon.shield },
          { to: '/information-security?tab=appointments', labelKey: 'infosec_appointments', icon: Icon.shield },
          { to: '/information-security?tab=procedure', labelKey: 'infosec_procedure', icon: Icon.shield },
        ],
      },
      // 2026-06-03 — Glossary & Terminology moved here from
      // Resources per Shane.
      { to: '/glossary', labelKey: 'glossary', icon: Icon.glossary },
    ],
  },
  {
    titleKey: 'people_hr',
    items: [
      // 2026-06-28 — collapsible parent: the 6 tabs drop into the sidebar.
      {
        to: '',
        labelKey: 'hr_people',
        icon: Icon.hr,
        children: [
          { to: '/hr?tab=employees', labelKey: 'hr_employees', icon: Icon.hr },
          { to: '/hr?tab=changes', labelKey: 'hr_changes', icon: Icon.hr },
          /* Pharmacy only - hrChildrenFor drops it for everybody else. */
          { to: '/hr?tab=gphc', labelKey: 'hr_gphc', icon: Icon.hr },
          { to: '/hr?tab=policies', labelKey: 'hr_policies', icon: Icon.hr },
          { to: '/hr?tab=memos', labelKey: 'hr_memos', icon: Icon.hr },
          { to: '/hr?tab=claims', labelKey: 'hr_claims', icon: Icon.hr },
          { to: '/hr?tab=employees_left', labelKey: 'hr_employees_left', icon: Icon.hr },
          /*
           * RECRUITMENT MOVED HERE FROM PHARMACY (Reagan, 2026-09-25). Hiring
           * is not a pharmacy idea — every tenant does it — and it sat under
           * Pharmacy only because that is where it was first needed.
           *
           * It sits above Import / Export deliberately: this list runs roughly
           * in the order somebody works through it, and import/export is the
           * housekeeping at the end rather than a step in the middle.
           *
           * /api/recruitment is gated to hr_people and the recruitment sub-tab
           * in app.ts, so the API refuses whoever this item is hidden from.
           */
          { to: '/recruitment', labelKey: 'recruitment', icon: Icon.hr },
          { to: '/hr?tab=importexport', labelKey: 'hr_importexport', icon: Icon.hr },
        ],
      },
      // 2026-06-03 — Compliance Gate moved to the Admin hub (setup
      // page). Removed from the sidebar. Route /gate is unchanged.
      // 2026-06-28 — collapsible parent. Shift Management is filtered out in
      // the render when the tenant has shiftManagementEnabled === false
      // (e.g. A live tenant), so those tenants see Shift Builder / Timesheets only —
      // matching the page's conditional tab.
      //
      // 2026-08-26 (SPEC-47) — Allocate Shifts removed. It looked like weekly
      // scheduling and was not: it wrote `shift_patterns`, a person's
      // contractual working week, which the Shift Builder, payroll, the RTI
      // submission and holiday entitlement all read. Being optional and in the
      // wrong place, nine of a live tenant's sixty-four live employees had no pattern
      // at all. It now lives on the employee's own Employment tab, and the
      // Shift Builder says who is still missing one.
      {
        to: '',
        labelKey: 'rota_shifts',
        icon: Icon.rota,
        children: [
          { to: '/rota?tab=rota', labelKey: 'rota_builder', icon: Icon.rota },
          { to: '/rota?tab=shifts', labelKey: 'rota_shift_management', icon: Icon.rota },
          { to: '/rota?tab=timesheets', labelKey: 'rota_timesheets', icon: Icon.rota },
        ],
      },
      // 2026-06-28 — collapsible parent: the 7 tabs drop into the sidebar.
      {
        to: '',
        labelKey: 'training_records',
        icon: Icon.training,
        children: [
          { to: '/training?tab=matrix', labelKey: 'training_matrix', icon: Icon.training },
          { to: '/training?tab=courses', labelKey: 'training_courses', icon: Icon.training },
          { to: '/training?tab=toolbox', labelKey: 'training_toolbox', icon: Icon.training },
          { to: '/training?tab=fleet_toolbox', labelKey: 'training_fleet_toolbox', icon: Icon.training },
          { to: '/training?tab=certificates', labelKey: 'training_certificates', icon: Icon.training },
          { to: '/training?tab=importexport', labelKey: 'training_importexport', icon: Icon.training },
        ],
      },
      // 2026-06-28 — collapsible parent: the 5 tabs drop into the sidebar.
      // The count badge is carried onto the parent button by renderItemBadge.
      {
        to: '',
        labelKey: 'holiday',
        icon: Icon.holiday,
        children: [
          { to: '/holiday?tab=calendar', labelKey: 'holiday_calendar', icon: Icon.holiday },
          { to: '/holiday?tab=requests', labelKey: 'holiday_requests', icon: Icon.holiday },
          { to: '/holiday?tab=entitlements', labelKey: 'holiday_entitlements', icon: Icon.holiday },
          { to: '/holiday?tab=bradford', labelKey: 'holiday_bradford', icon: Icon.holiday },
          { to: '/holiday?tab=importexport', labelKey: 'holiday_importexport', icon: Icon.holiday },
        ],
      },
      /*
       * Appraisals & Discipline — between Holiday and Health & Safety
       * (Shane, 2026-08-21).
       *
       * Appraisals MOVED here out of Training Records. How someone is doing
       * belongs beside the disciplinary record, not beside the course
       * catalogue — and at a tribunal the two are read together.
       */
      {
        to: '',
        labelKey: 'appraisals_discipline',
        icon: Icon.hr,
        children: [
          {
            to: '/appraisals-discipline?tab=appraisals',
            labelKey: 'ad_appraisals',
            icon: Icon.hr,
          },
          {
            to: '/appraisals-discipline?tab=discipline',
            labelKey: 'ad_discipline',
            icon: Icon.hr,
          },
          {
            to: '/appraisals-discipline?tab=grievance',
            labelKey: 'ad_grievance',
            icon: Icon.hr,
          },
        ],
      },
      // 2026-06-28 — collapsible parent: the 10 tabs drop into the sidebar.
      {
        to: '',
        labelKey: 'health_safety',
        icon: Icon.hs,
        children: [
          { to: '/hs?tab=incidents', labelKey: 'hs_incidents', icon: Icon.hs },
          { to: '/hs?tab=ra', labelKey: 'hs_ra', icon: Icon.hs },
          { to: '/hs?tab=fireregister', labelKey: 'hs_fireregister', icon: Icon.hs },
          { to: '/hs?tab=fire', labelKey: 'hs_fire', icon: Icon.hs },
          { to: '/hs?tab=firetesting', labelKey: 'hs_firetesting', icon: Icon.hs },
          { to: '/hs?tab=coshh', labelKey: 'hs_coshh', icon: Icon.hs },
          { to: '/hs?tab=permits', labelKey: 'hs_permits', icon: Icon.hs },
          { to: '/hs?tab=contractors', labelKey: 'hs_contractors', icon: Icon.hs },
          { to: '/hs?tab=statutorySurveys', labelKey: 'hs_statutory', icon: Icon.hs },
          { to: '/hs?tab=firstaid', labelKey: 'hs_firstaid', icon: Icon.hs },
          { to: '/hs?tab=visitors', labelKey: 'hs_visitors', icon: Icon.hs },
          { to: '/hs?tab=siteinduction', labelKey: 'hs_siteinduction', icon: Icon.hs },
          { to: '/hs?tab=importexport', labelKey: 'hs_importexport', icon: Icon.hs },
        ],
      },
    ],
  },
  {
    titleKey: 'operations',
    items: [
      /*
       * 2026-08-22 — ONE Operations group. Shane: "the crm, enquiries, jobs,
       * stock suppliers should be part of the operation so instead of the 2
       * sections we have 1 OPERATIONS".
       *
       * Sales & Projects & Stock is gone and its five sections are here. A
       * manufacturing tenant was seeing "Operations" holding only Fleet while
       * the actual work sat under a separate heading - a split that existed on
       * their screen for no reason.
       *
       * Order is his: sell, then do, then operate, then supply. Group size is
       * not a worry - a tenant only ever sees the sections it runs, because
       * hiddenSections, feature flags and sector gating drop the rest and an
       * empty group drops itself.
       */

      /*
       * THE MAIL ROOM (2026-10-13) — first in Operations, because this is where
       * the work arrives. Shane's order for this group is sell, then do, then
       * operate, then supply; a dropped email is upstream of all four, since
       * nobody knows yet whether it is a customer, an enquiry or a problem.
       *
       * It sits ABOVE Customer CRM deliberately. Putting it below would read as
       * a CRM feature, and it is not: an issue flagged here may never involve a
       * customer record at all.
       */
      {
        to: '',
        labelKey: 'front_desk',
        icon: Icon.mail,
        children: [
          { to: '/front-desk?tab=inbox', labelKey: 'fd_inbox', icon: Icon.mail },
          { to: '/front-desk?tab=issues', labelKey: 'fd_issues', icon: Icon.mail },
          { to: '/front-desk?tab=archive', labelKey: 'fd_archive', icon: Icon.mail },
        ],
      },
      // 2026-06-28 — collapsible parent: the 5 tabs drop into the sidebar.
      {
        to: '',
        labelKey: 'customer_crm',
        icon: Icon.enquiries,
        children: [
          { to: '/customer-crm?tab=customers', labelKey: 'crm_customers', icon: Icon.enquiries },
          { to: '/customer-crm?tab=onboarding', labelKey: 'crm_onboarding', icon: Icon.enquiries },
          /*
           * BELOW ONBOARDING, where Shane asked for it (2026-08-31): *"so we
           * have the customer complains log on the side bar below on boarding..
           * that is a the place to log a complaing"*.
           *
           * The logging already existed and could not be found: it was a button
           * labelled "Feedback" in a customer row's actions, so you had to know
           * it was there and open a customer to reach it. Nothing in the sidebar
           * said complaints existed, while the ISO Customer Feedback screen sat
           * there telling you to *"log feedback in CRM"*.
           *
           * NOT GATED ON ISO. Shane: *"if you are not ISO 9001 then we can log
           * complaints.. if you are ISO9001 then your logging and then it goes
           * to the ISO"*. A complaints log is ordinary good practice for any
           * business; the clause-9.1.2 filing is what the standard adds on top.
           */
          { to: '/customer-crm?tab=complaints', labelKey: 'crm_complaints', icon: Icon.enquiries },
          // Approval Workflow removed 2026-08-19 (Shane) — it asked the same
          // questions the Onboarding record already answers, with ticks that
          // were never stored. Approval now lives on that record, derived.
          /*
           * The monthly credit limit review worklist (Kris, accountant, 2026-09-02):
           * *"it would be good to be able to export all the credit reveiws due or
           * overdue so they casn be populated in one screen that feeds the customer
           * record"*.
           *
           * ABOVE Import / Export, because it is work to be done rather than a data
           * tool - the same reason Complaints sits where it does.
           */
          { to: '/customer-crm?tab=creditreviews', labelKey: 'crm_creditreviews', icon: Icon.enquiries },
          { to: '/customer-crm?tab=importexport', labelKey: 'crm_importexport', icon: Icon.enquiries },
        ],
      },
      // 2026-06-28 — collapsible parent, tier-aware. Enquiries + Quotations
      // show on both tiers; Base adds Sales Pipeline, Customised swaps in
      // Customers (manufacturing/Panelbuilder view). Red unassigned badge
      // rides the parent.
      /*
       * STANDARD SALES & JOBS - SPEC-51, made a collapsible parent in Stage 8.
       *
       * The standard operational vertical: enquiry, quotation, job, invoice. A
       * tenant who asked for no sector gets this one; a tenant on Pharmacy,
       * Motor Trade or Switchboard Builder gets theirs instead. Shane,
       * 2026-08-29: *"they are their own true verticals"*, *"no shared
       * screens"*.
       *
       * PLAIN WORDS, and that is the point of the section. The switchboard
       * branch below renames the same shape to describe its own work - Sales,
       * Engineering, Sales Enquiries. Shane asked Standard to carry "plain
       * references NOT THE SITE WORK reference in the switchboard builder",
       * because this is what somebody sees who has not asked for a sector.
       *
       * One page, seven leaves, each a `?tab=`. The tab row moved out of the
       * page to get here, which is the pattern every other section with a
       * dropdown follows - so back and forward move between tabs, and a leaf
       * links straight to one.
       *
       * NOTHING BELOW IS SHARED WITH THE SWITCHBOARD. Its branch keeps every
       * screen it had; these are Standard's own routes over Standard's own
       * data.
       */
      {
        to: '',
        labelKey: 'standard_sales',
        icon: Icon.quotes,
        children: [
          { to: '/standard-sales?tab=intake', labelKey: 'std_intake', icon: Icon.quotes },
          { to: '/standard-sales?tab=enquiries', labelKey: 'std_enq', icon: Icon.quotes },
          { to: '/standard-sales?tab=quotations', labelKey: 'std_qt', icon: Icon.quotes },
          { to: '/standard-sales?tab=jobs', labelKey: 'std_jobs', icon: Icon.jobs },
          { to: '/standard-sales?tab=pipeline', labelKey: 'std_pipeline', icon: Icon.quotes },
          { to: '/standard-sales?tab=engineers', labelKey: 'std_eng', icon: Icon.jobs },
          { to: '/standard-sales?tab=customers', labelKey: 'std_cust', icon: Icon.enquiries },
        ],
      },
      /*
       * SALES (2026-10-13) — the OFFICE sector's version of the above.
       *
       * Reagan: *"this would be for customers that only need sales that dont
       * have jobs"*. Enquiries and quotations, and who is carrying what. No
       * Jobs, because a business selling a service has nothing to track after
       * the sale; no Intake, because the Front Desk is where work arrives; no
       * Customers, because the CRM two rows up owns those.
       *
       * The office sector's preset gives Sales; the others give Standard Sales
       * & Jobs above. The routes underneath are the same ones — same enquiries,
       * same quotations, one set of records.
       */
      {
        to: '',
        labelKey: 'sales',
        icon: Icon.quotes,
        children: [
          { to: '/sales?tab=enquiries', labelKey: 'sales_enq', icon: Icon.quotes },
          { to: '/sales?tab=quotations', labelKey: 'sales_qt', icon: Icon.quotes },
          { to: '/sales?tab=team', labelKey: 'sales_team', icon: Icon.hr },
          /*
           * ONBOARDING (2026-10-13). Reagan: *"i just want sales people to
           * deal with them then i can log on and see who i need to sort
           * tennants out for"*. A worklist of the companies that have said
           * yes and are not on the platform yet — it sets nothing up itself.
           */
          { to: '/sales?tab=onboarding', labelKey: 'sales_onboarding', icon: Icon.globe },
          /*
           * THE DIARY (2026-10-14). Reagan: *"this can be on the sales
           * section as calander ... we can book demos in with assigned who
           * will do it from the sales team and also can book onsite
           * onboarding and offsite onbaording"*.
           */
          { to: '/sales?tab=calendar', labelKey: 'sales_calendar', icon: Icon.schedule },
          { to: '/sales?tab=pricelist', labelKey: 'sales_pricelist', icon: Icon.cashflow, operatorOnly: true },
        ],
      },
      // Marketing: the library of marketing files, planned social posts and their calendar.
      {
        to: '',
        labelKey: 'marketing',
        icon: Icon.globe,
        children: [
          { to: '/marketing?tab=library', labelKey: 'mkt_library', icon: Icon.docRegister },
          { to: '/marketing?tab=posts', labelKey: 'mkt_posts', icon: Icon.globe },
          { to: '/marketing?tab=designs', labelKey: 'mkt_designs', icon: Icon.docRegister },
          { to: '/marketing?tab=calendar', labelKey: 'mkt_calendar', icon: Icon.schedule },
          { to: '/marketing?tab=accounts', labelKey: 'mkt_accounts', icon: Icon.globe },
        ],
      },
      /*
       * ENQUIRIES AND QUOTATIONS, AND JOBS AND PROJECTS, RETIRED HERE
       * (2026-08-29, SPEC-51 Stage 10). Shane: *"take them off please"*.
       *
       * They were the generic sales-and-jobs sections every tenant carried.
       * Standard Sales & Jobs above replaces them outright for a tenant with no
       * operational vertical of its own, and a tenant WITH one runs that one
       * instead - so nobody is left without a way to raise an enquiry, quote it
       * and turn it into a job.
       *
       * Removed from all three places a section is declared: here, the admin
       * catalogue, and SIDEBAR_SECTION_KEYS. section-catalogue-guard requires
       * those three to agree, which is what stops a half-removal.
       *
       * THE ROUTES AND THE API STAY, and that is not an oversight.
       * `/enquiries-quotations` and `/jobs` are still reached with
       * `?build=switchboard` from the Switchboard Builder branch below, which
       * keeps every screen it had. What has gone is the plain, unparameterised
       * entry - the duplicate that showed the same rows under a second heading.
       */
      // 2026-06-28 — collapsible parent, tier-additive. Base: Summary + Admin;
      // Plus adds PO Tracker + Jobs; Customised adds Usage + Barcode. The
      // in-page brand tab bar (secondary) stays on the page.

      // 2026-06-28 — collapsible parent: the 4 tabs drop into the sidebar.
      // The building selector stays on the page; the count badge rides the
      // collapsible parent via renderItemBadge.
      // Plant & Equipment and Building Maintenance moved OUT of Operations on
      // 2026-08-20 into the new Assets & Maintenance group below.
      // 2026-06-28 — collapsible parent. Children = the full Plus tab set;
      // effectiveChildren() trims the Plus-only tabs for Base tenants and the
      // TM-only "My Credentials" child for non-TM users. Fleet Admin shows on
      // both tiers; Transport Manager Admin is Plus-only. Count badge via the
      // renderItemBadge helper.
      {
        to: '',
        labelKey: 'fleet_transport',
        icon: Icon.fleet,
        children: [
          { to: '/fleet?tab=gps', labelKey: 'fleet_gps', icon: Icon.fleet },
          { to: '/fleet?tab=vehicles', labelKey: 'fleet_vehicles', icon: Icon.fleet },
          { to: '/fleet?tab=drivers', labelKey: 'fleet_drivers', icon: Icon.fleet },
          // Fleet Equipment Register moved OUT of here on 2026-08-20 into the
          // new Assets & Maintenance group, where it is its own section.
          // Shane: *"we are not doubling up on sidebar tabs... 1 area is where
          // it comes from"* — the slings, lift bars and hiab are equipment that
          // needs inspecting, so they belong with the other equipment registers.
          // Referencing them from a Fleet page is still fine; a second sidebar
          // entry is not.
          { to: '/fleet?tab=journeys', labelKey: 'fleet_journeys', icon: Icon.fleet },
          { to: '/fleet?tab=tacho', labelKey: 'fleet_tacho', icon: Icon.fleet },
          { to: '/fleet?tab=sitemanager', labelKey: 'fleet_sitemanager', icon: Icon.fleet },
          { to: '/fleet?tab=siteworks', labelKey: 'fleet_siteworks', icon: Icon.fleet },
          { to: '/fleet?tab=incidents', labelKey: 'fleet_incidents', icon: Icon.fleet },
          { to: '/fleet?tab=fleetadmin', labelKey: 'fleet_fleetadmin', icon: Icon.fleet },
          { to: '/fleet?tab=tmadmin', labelKey: 'fleet_tmadmin', icon: Icon.fleet },
          { to: '/fleet?tab=fuel', labelKey: 'fleet_fuel', icon: Icon.fleet },
          { to: '/fleet?tab=eu561', labelKey: 'fleet_eu561', icon: Icon.fleet },
          { to: '/fleet?tab=importexport', labelKey: 'fleet_importexport', icon: Icon.fleet },
          { to: '/fleet?tab=tmcredentials', labelKey: 'fleet_tmcredentials', icon: Icon.fleet },
        ],
      },
      // 2026-06-03 — Accreditations moved up into the Administration
      // group per Shane.
      // 2026-06-28 — collapsible parent: the 6 tabs drop into the sidebar.
      // The building selector stays on the Asset Register page. Overdue
      // statutory-inspection badge rides the parent via renderItemBadge.
      // (Building Maintenance now lives in the Assets & Maintenance group.)
      // 2026-05-26 — Jobs & Projects moved out of Operations into
      // Sales & Stock (between Enquiries & Quotations and Stock
      // Management) per Shane: the flow is CRM → Quote → Job → Stock,
      // so the sidebar should mirror that order rather than treating
      // Jobs as an operational concern.
      { to: '/stub/hire-management', labelKey: 'hire_management', icon: Icon.hire },
      { to: '/stub/lettings-management', labelKey: 'lettings_management', icon: Icon.lettings },
      // 2026-06-22 — Care Plans removed + Pharmacy moved here from Sales & Stock per
      // Shane (Care Plans superseded by Pharmacy). ONE collapsible entry whose modules
      // nest beneath it (native PharmOS port). Hidden per-tenant via hiddenSections
      // ('pharmacy').
      {
        to: '',
        labelKey: 'pharmacy',
        icon: Icon.care,
        // 2026-07-23 — the pharmacy vertical grew to ~40 modules. The full
        // catalogue now lives on the /pharmacy hub (categorised tile banks,
        // PharmacyHubPage); the sidebar keeps only the most-used shortcuts plus
        // an "All pharmacy modules →" link to the hub. Every module route still
        // exists and is reachable from the hub — this is presentation only.
        children: [
          // Gateway to the full ~40-module catalogue (the /pharmacy hub) plus the
          // two daily overview dashboards. Every individual module lives on the
          // hub — the sidebar deliberately does NOT re-list them (2026-08-13).
          /*
           * The four banks, in CATEGORIES' order. Each opens on its first
           * module and stays lit across all of them; that bank's modules are
           * the strip across the top of the page (PharmacyModuleStrips).
           */
          ...CATEGORIES.filter((b) => (b.tiles ?? []).length > 0).map((b) => ({
            to: (b.tiles ?? [])[0]!.to,
            labelKey: `pharmacy_bank_${b.id}`,
            icon: Icon.auditPack,
            activeWhenIn: (b.tiles ?? [])
              .map((m) => m.to)
              .filter((m) => !CATALOGUED_BUT_OWN_SCREEN.has(m.split('?')[0]!)),
          })),
          /*
           * THE ROTA SHORTCUT IS GONE (Reagan, 2026-09-25), and the hub's Team
           * bank that also pointed at it went with it. The platform has Shifts
           * & Timesheets, and pharmacy shifts are to be sorted onto that rather
           * than run beside it. The comment this replaces argued the shortcut
           * was earned because the rota is a daily-use tool — which is true,
           * and is the argument for it being the PLATFORM rota rather than a
           * second one.
           *
           * /pharmacy-rota still exists and still works; it is no longer linked,
           * so nothing is lost while the integration is scheduled.
           */
          /*
           * CARE HOME IS ITS OWN SECTION (Reagan, 2026-09-25), not a link into
           * the hub and not the audits screen alone.
           *
           * It was 'Care Home Audits' — one screen of nine, with the other
           * eight reachable only by going into the hub, whose sidebar link is
           * labelled "Dispensary Management". Care-home work is not dispensary
           * management, so anybody supplying homes had to already know their
           * screens were hidden in there. They open from here now.
           *
           * CARE_HOME_MODULES is the single list: the hub's Care Homes bank was
           * removed in the same change, so these nine are named in one place
           * rather than two that drift.
           */
          /*
           * IT POINTS AT ITS OWN FIRST LEAF, and must. A nested branch parent
           * renders as a NavLink (renderTabChildren), not as the toggle button
           * a TOP-LEVEL parent gets — so `to: ''` made a link to nowhere and
           * clicking Care Home showed a blank page. Reagan found it the moment
           * he opened the branch again, which the breadcrumb test could not:
           * resolveNavTrail handles three levels quite happily and said the
           * nav was fine.
           *
           * Receive Paperwork is the right first leaf for the same reason it is
           * first in the list: the reorder sheet and the scripts arrive before
           * anybody opens a cycle. ISO 9001 and Switchboard Sales are declared
           * the same way.
           */
          /*
           * ONE ROW, AND IT STAYS LIT INSIDE THE SECTION (2026-09-29). The nine
           * children are the in-page strip now — see careHomeModules.ts. The
           * strip navigates, so the path leaves /care-home-receive while the
           * screen is visibly the same shell; `activeWhenIn` is what keeps this
           * row highlighted through that, exactly as Dispensary Management does.
           */
          { to: '/care-home-receive', labelKey: 'care_home', icon: Icon.auditPack, activeWhenIn: CARE_HOME_PATHS },
          { to: '/locum-ledger', labelKey: 'locum_ledger', icon: Icon.auditPack },
          /*
           * Below Locum Hub, where Reagan asked for it twice. It is not a hub
           * tile: moving a pharmacy's records in is something you do when you
           * arrive and then rarely, so it belongs where you can find it rather
           * than in a bank of daily work.
           */
          { to: '/pharmacy-import-export', labelKey: 'pharmacy_importexport', icon: Icon.auditPack, activeWhenIn: ['/pmr-import'] },
          // Workforce shortcuts — the 'Workforce & Locum' hub bank was retired in
          // favour of these direct sidebar entries (2026-08-14). Recruitment was
          // one of them until 2026-09-25, when it moved to HR & People: every
          // tenant hires, and it was here only because this is where it was
          // first wanted. See the note beside it there.
          /*
           * LOCUM PORTAL REMOVED FROM THE SIDEBAR (Reagan, 2026-09-30).
           *
           * It is not a staff screen. `/locum-portal` renders inside the
           * tenant AppShell and is the ADMIN PREVIEW of a locum's own portal,
           * opened against a named locum with `?locumName=`. What a locum
           * actually uses is `/locum-app`, its own auth realm, outside this
           * shell entirely — see pages/locumApp/LocumAppPage.tsx. A door into
           * a preview of somebody else's screen does not belong beside the
           * pharmacy's own work.
           *
           * THE ROUTE IS NOT REMOVED. App.tsx still serves it and the page
           * still works; it is reachable by URL, with `?locumName=` naming
           * the locum to preview.
           *
           * SAID PLAINLY BECAUSE THIS EXACT REMOVAL WENT WRONG BEFORE. The
           * /pharmacy-rota link was taken out on 2026-09-25 pending an
           * integration that never happened, and a working, gated, daily-use
           * tool went dark — then got asked for again five days later as new
           * work, because an unlinked feature reads exactly like an absent
           * one. The sidebar is currently the ONLY door to /locum-portal, so
           * after this it has none. That is the intended change, and the way
           * back is this comment.
           */
          /*
           * TRAINING & CPD REMOVED (Reagan, 2026-09-28). It pointed at bare
           * `/training` — the same URL as Training Records' first tab — so it
           * was never a pharmacy screen at all, just a second door onto the
           * platform's Training Matrix under a different name.
           *
           * The collision was already on the record: nav-trail.test.ts carried
           * it as the sole KNOWN_COLLISIONS entry, logged for Shane as
           * NAV-TRAINING-ROUTE-COLLISION-001, because two sidebar rows sharing
           * one URL meant the header could only ever name one of them. Removing
           * the duplicate is what closes it, so that exclusion goes too and the
           * audit is strict again with nothing excused.
           *
           * Pharmacy CPD lines up with the Training Matrix properly at a later
           * date; nothing about /training or its data changed here.
           */
          /*
           * WATCHTOWER AND IG COMPLIANCE REMOVED (2026-09-28). They were the
           * last two sidebar rows that also sat in a hub bank — Reagan flagged
           * them as duplicates on 25 Sep. Both are in the hub's Governance &
           * Quality bank, so they are reached through it and its strips like
           * every other module in that bank.
           */
        ],
      },
      // Collapsible parent, mirroring the Forecourt nav shape. Only the
      // destinations that exist today are listed — further sections are added
      // here as they are built, so the sidebar never carries a dead link.
      {
        to: '',
        labelKey: 'motor_trade',
        icon: Icon.fleet,
        children: [
          { to: '/motor-trade?tab=stock', labelKey: 'motor_trade_stock', icon: Icon.fleet },
          {
            to: '/motor-trade?tab=sourcing',
            labelKey: 'motor_trade_sourcing',
            icon: Icon.fleet,
          },
          {
            to: '/motor-trade?tab=workshop',
            labelKey: 'motor_trade_workshop',
            icon: Icon.fleet,
          },
          // Deliveries & Collections sat here until 2026-08-20. It is now a
          // sub-tab of the Diary — a collection is a thing with a date on it —
          // and a second sidebar entry into the same page would be two doors
          // into one room. ?tab=deliveries still resolves, so existing links
          // and bookmarks keep working.
          {
            to: '/motor-trade?tab=logistics',
            labelKey: 'motor_trade_logistics',
            icon: Icon.fleet,
          },
          {
            to: '/motor-trade?tab=finance',
            labelKey: 'motor_trade_finance',
            icon: Icon.fleet,
          },
          {
            to: '/motor-trade?tab=reminders',
            labelKey: 'motor_trade_reminders',
            icon: Icon.fleet,
          },
          /*
           * Reports MOVED to ERP & Accounts → Reporting on 2026-10-13 (Reagan:
           * "for the reports section this needs to be in the accounts section
           * for motor trade tenants only").
           *
           * It is a SUB-TAB there and has no nav row of its own, by the same
           * decision — the Reporting row is the way in. The old ?tab=reports
           * link redirects to it, so anything bookmarked still arrives.
           */
          { to: '/motor-trade?tab=settings', labelKey: 'motor_trade_settings', icon: Icon.fleet },
        ],
      },
      /*
       * HOSPITALITY (2026-08-16, Shane: "the trunc page you just built is in
       * the hospitality sidebar").
       *
       * Tronc first landed under Finance beside Cashflow and Audit Pack,
       * because those are the other paid Finance add-ons. Wrong: tips and
       * service charge are a hospitality thing, and hospitality is a SECTOR —
       * it belongs here with Pharmacy and Motor Trade, not in Finance.
       *
       * Only Tronc is listed because only Tronc exists, following the Motor
       * Trade note above: "further sections are added here as they are built,
       * so the sidebar never carries a dead link".
       *
       * GATED ON troncEnabled, the add-on. When hospitality proper is built,
       * move this flag down onto the Tronc child, where it belongs.
       */
      {
        to: '',
        labelKey: 'hospitality',
        icon: Icon.payroll,
        tenantFlag: 'troncEnabled',
        children: [
          { to: '/tronc', labelKey: 'tronc', icon: Icon.payroll },
        ],
      },

      /*
       * SWITCHBOARD BUILDER (2026-08-22) - its own section, like Motor Trade.
       *
       * Shane: *"if we have a switchboard builder (like motor trade or
       * pharmacy) then its seperate and there would be no plus or customised
       * to select for now unless we develop them further"*.
       *
       * It was the Customised TIER of Enquiries & Quotations, which meant one
       * customer's bespoke build arrived by picking a price band. As a section
       * it is granted per tenant and can be sold to a second customer by
       * ticking a box.
       */
      {
        to: '',
        labelKey: 'switchboard_builder',
        icon: Icon.enquiries,
        /*
         * Same screens, distinct URLs. Pointing these straight at /enquiries
         * and /jobs put TWO sidebar entries on one route, and the breadcrumb
         * resolver - which scans the whole nav with no idea which sections a
         * tenant holds - started trailing /jobs under Switchboard Builder for
         * everyone. Caught by nav-trail.test.ts.
         *
         * The parameter also gives the pages a signal to work with when the
         * switchboard flow grows its own view.
         */
        /*
         * Two branches of one flow, each with its own screens. Shane,
         * 2026-08-22: *"in the switchboard builder change the name of Enquires
         * and Quotations to Sales, Jobs and projects to engineering"* - names
         * that describe the switchboard company's work rather than
         * cross-referencing the generic sections a normal tenant gets.
         *
         * Every leaf carries `build=switchboard`, which is what keeps this
         * branch distinct from the plain sections living on the same routes.
         * The trail resolver picks the most specific match, so a URL with the
         * parameter resolves here and one without resolves to the plain
         * section.
         */
        children: [
          {
            /*
            * The branch carries no ?tab= of its own, so it owns a URL its
            * leaves do not. Pointing it at its own first leaf gave two rows one
            * URL: the breadcrumb resolved the branch to "Enquiries" and never
            * named it. The page picks its default tab from here.
            */
            to: '/enquiries-quotations?build=switchboard',
            labelKey: 'sb_sales',
            icon: Icon.enquiries,
            children: [
              { to: '/enquiries-quotations?tab=enq&build=switchboard', labelKey: 'sb_enq', icon: Icon.quotes },
              { to: '/enquiries-quotations?tab=qt&build=switchboard', labelKey: 'sb_qt', icon: Icon.quotes },
              { to: '/enquiries-quotations?tab=cust&build=switchboard', labelKey: 'eq_cust', icon: Icon.quotes },
              { to: '/enquiries-quotations?tab=sublib&build=switchboard', labelKey: 'eq_sublib', icon: Icon.quotes },
              { to: '/enquiries-quotations?tab=quotelib&build=switchboard', labelKey: 'eq_quotelib', icon: Icon.quotes },
              { to: '/enquiries-quotations?tab=omstd&build=switchboard', labelKey: 'eq_omstd', icon: Icon.quotes },
            ],
          },
          {
            to: '/jobs?build=switchboard',
            labelKey: 'sb_engineering',
            icon: Icon.jobs,
            children: [
              { to: '/jobs?tab=eng&build=switchboard', labelKey: 'jobs_eng', icon: Icon.jobs },
              { to: '/jobs?tab=board&build=switchboard', labelKey: 'jobs_board', icon: Icon.jobs },
              { to: '/jobs?tab=site&build=switchboard', labelKey: 'jobs_site', icon: Icon.jobs },
              { to: '/jobs?tab=prep&build=switchboard', labelKey: 'jobs_prep', icon: Icon.jobs },
              { to: '/jobs?tab=tablet&build=switchboard', labelKey: 'jobs_tablet', icon: Icon.jobs },
            ],
          },
          /*
           * IMPORT / EXPORT (Shane, 2026-08-26) — the third child, alongside
           * Sales and Engineering.
           *
           * Here rather than as buttons in the settings modal, whose footer is
           * for Save and Cancel. CRM and Stock already carry a leaf of exactly
           * this shape (`?tab=importexport`), so this follows the platform's
           * pattern instead of inventing one.
           *
           * It carries `build=switchboard` like every leaf in this branch,
           * which is what keeps it distinct from the plain Enquiries section
           * living on the same route and lets the breadcrumb resolve it here.
           */
          {
            to: '/enquiries-quotations?tab=importexport&build=switchboard',
            labelKey: 'sb_importexport',
            icon: Icon.quotes,
          },
          // Packages from the Switchboard master, decided item by item (SPEC-56).
          {
            to: '/enquiries-quotations?tab=incoming&build=switchboard',
            labelKey: 'sb_incoming',
            icon: Icon.quotes,
            roles: ['director', 'manager'],
          },
        ],
      },

      /* Supply - the far end of the same operation. Moved 2026-08-22. */
      {
        to: '',
        labelKey: 'stock_management',
        icon: Icon.stock,
        children: [
          {
            to: '/stock?tab=summary',
            labelKey: 'stock_summary',
            icon: Icon.stock,
            // Parts Catalogue and Stock Movements are TABS of this page, not
            // rows of their own — so this row stays lit while you are on them.
            activeTabs: ['parts', 'movements'],
            /*
             * 2026-08-25 — and so are the BRAND catalogues, now that the brand
             * strip lives on Parts Catalogue. Their ids are per-tenant database
             * rows, so they cannot be listed above; every other Stock view has
             * its own row, so "the tabs nobody pins" is exactly the brands.
             *
             * Without this, clicking a brand dropped the highlight — the same
             * complaint that produced activeTabs, one level deeper.
             */
            claimsUnpinnedTabs: true,
          },
          { to: '/stock?tab=admin', labelKey: 'stock_admin', icon: Icon.stock },
          { to: '/stock?tab=potracker', labelKey: 'stock_potracker', icon: Icon.stock },
          { to: '/stock?tab=jobs', labelKey: 'stock_jobs', icon: Icon.stock },
          { to: '/stock?tab=usage', labelKey: 'stock_usage', icon: Icon.stock },
          { to: '/stock?tab=barcode', labelKey: 'stock_barcode', icon: Icon.stock },
          /* 2026-08-25 — RESTORED. Parts Catalogue, Stock Movements and Goods
             Received were built in Session 3.1 and then orphaned when Stock was
             rebuilt around ProStockView (2026-06-28): the components and all
             four of their endpoints survived, but nothing rendered them, so no
             tier and no permission could reach them.

             Parts Catalogue and Stock Movements are deliberately NOT rows here.
             Reagan: "the side bar parts catalouge gets taken off as this is in
             the parts catalouge section in summary. the same with stock
             movements." They are tabs at the top of the Stock page instead
             (STOCK_TABS in ProStockView) — one way in, not two. The routes
             /stock?tab=parts and ?tab=movements still work, so old links and
             bookmarks are unaffected.

             Goods Received stays a row: it is Plus+ gated and not part of that
             trio. */
          { to: '/stock?tab=grn', labelKey: 'stock_grn', icon: Icon.stock },
          { to: '/stock?tab=importexport', labelKey: 'stock_importexport', icon: Icon.stock },
        ],
      },
      // 2026-06-03 — Approved Suppliers moved here from Finance per
      // Shane.
      // 2026-06-28 — collapsible parent: the 4 tabs drop into the sidebar.
      // Amber overdue-review count badge rides the parent via renderItemBadge.
      {
        to: '',
        labelKey: 'approved_suppliers',
        icon: Icon.suppliers,
        children: [
          { to: '/approved-suppliers?tab=register', labelKey: 'as_register', icon: Icon.suppliers },
          { to: '/approved-suppliers?tab=onboarding', labelKey: 'as_onboarding', icon: Icon.suppliers },
          { to: '/approved-suppliers?tab=performance', labelKey: 'as_performance', icon: Icon.suppliers },
          { to: '/approved-suppliers?tab=importexport', labelKey: 'as_importexport', icon: Icon.suppliers },
        ],
      },
    ],
  },
  /*
   * Assets & Maintenance (Shane, 2026-08-20) — ASSETS-MAINTENANCE-001.
   *
   * Plant & Equipment and Building Maintenance used to sit in Operations as two
   * unrelated-looking sections, and the naming actively misled: the "Plant &
   * Equipment Register" is `equipment_items` with register='building', while
   * "Building Maintenance" is the separate `building_assets` table. Two
   * different things called building, in two different sections. That is why
   * calibration bounced between them and why the fleet equipment register lived
   * on one page but was linked from another.
   *
   * A GROUP rather than a collapsible parent, because the sidebar renders only
   * two levels (group -> section -> flat tab links) and each of these needs to
   * keep its own tabs.
   *
   * Seven sections: Plant equipment, Fleet Equipment, Tools & equipment,
   * Calibration, Building, Vehicles, Maintenance Schedule.
   *
   *   Plant  — fork trucks, steel and copper machines, MEWPs, gantry cranes
   *   Tools  — grinders, drills, powder coating guns
   *   Fleet  — slings, lift bars, the hiab: the lifting gear on the trucks
   *
   * Plant, Tools and Fleet are the three values of `equipment_items.register`
   * (renamed from 'building' | 'fleet' on 2026-08-20 — 'building' read
   * backwards here, because the separate `building_assets` table holds the
   * actual building fabric). Existing rows were renamed to 'plant', not split:
   * nothing distinguished a forklift from a grinder.
   *
   * The separate Tools & Equipment register was folded back into Plant on
   * 2026-08-21 — Shane: *"2 identical sections... the plant & Maintenance and
   * the Tools & Equipment to me really are in the same category"*. They were
   * the same screen twice with a different filter, and nothing had ever been
   * filed under 'tools' on any tenant.
   */
  {
    titleKey: 'assets_maintenance',
    items: [
      {
        to: '',
        labelKey: 'plant_equipment',
        icon: Icon.plantMachine,
        children: [
          { to: '/plant?tab=register', labelKey: 'plant_register', icon: Icon.plant },
          { to: '/plant?tab=inspections', labelKey: 'plant_inspections', icon: Icon.plant },
          { to: '/plant?tab=importexport', labelKey: 'plant_importexport', icon: Icon.plant },
        ],
      },
      // Its own section now, not a tab under Plant and not a link under Fleet.
      // The slings, lift bars and hiab are equipment that needs inspecting, so
      // they sit with the other registers.
      {
        to: '/plant?tab=fleet_register',
        labelKey: 'plant_fleet_register',
        icon: Icon.fleet,
      },
      // Calibration is its own section, and deliberately NOT maintenance.
      // Shane: it is "tested to within a standard and certified to that and
      // says it is within tolerance" — certification against a tolerance, not
      // upkeep. Related to a service or an MOT; not the same discipline.
      {
        to: '/plant?tab=calibration',
        labelKey: 'plant_calibration',
        icon: Icon.calibration,
      },
      {
        to: '',
        labelKey: 'building_maintenance',
        icon: Icon.building,
        children: [
          { to: '/building-maintenance?tab=assets', labelKey: 'bm_assets', icon: Icon.building },
          // Maintenance Schedule left this section on 2026-08-20 - it is now the
          // cross-register schedule at /maintenance-schedule, covering plant, tools,
          // fleet equipment, calibration, vehicles AND building rather than building
          // alone. Keeping it here too would be the doubling-up Shane ruled out.
          // Best Practice Guide left the sidebar on 2026-08-20 - it opens from the
          // header as a Guidance panel, the same way Plant & Equipment does.
          { to: '/building-maintenance?tab=importexport', labelKey: 'bm_importexport', icon: Icon.building },
        ],
      },
      // Vehicles — the MAINTENANCE view. The vehicle register stays in Fleet,
      // because drivers, tacho and journeys are why Fleet exists. This is the
      // schedule filtered to vehicles, so there is one page and no second
      // register. Tax and insurance stay Fleet's; they are not maintenance.
      {
        to: '/maintenance-schedule?type=vehicle',
        labelKey: 'am_vehicles',
        icon: Icon.fleet,
      },
      // Everything due, across all six registers above.
      {
        to: '/maintenance-schedule',
        labelKey: 'am_schedule',
        icon: Icon.schedule,
      },
    ],
  },
  
  {
    // 2026-05-13 — Finance group expanded with 4 add-on modules between
    // ERP & Accounts and Approved Suppliers. Each gated by its tenant
    // flag (default false on the column — only tenants that have bought
    // in see them). Order matches Shane's spec:
    //   ERP & Accounts · Payroll · Sub Contract ·
    //   Cashflow & Financial Modelling · Audit Pack · Approved Suppliers.
    titleKey: 'finance',
    items: [
      // 2026-06-28 — collapsible parent: the 6 category tabs drop into the
      // sidebar; each category's sub-tab strip stays on the ERP page.
      {
        to: '',
        labelKey: 'erp_accounts',
        icon: Icon.erp,
        children: [
          { to: '/erp-accounts?tab=sales', labelKey: 'erp_sales', icon: Icon.erp },
          { to: '/erp-accounts?tab=purchase', labelKey: 'erp_purchase', icon: Icon.erp },
          { to: '/erp-accounts?tab=banking-cash', labelKey: 'erp_banking', icon: Icon.erp },
          /* Keys kept from Ledger & Journals and Data Migration so a user's saved sub-tab blocks still apply. */
          { to: '/erp-accounts?tab=accounting', labelKey: 'erp_ledger', icon: Icon.erp },
          { to: '/erp-accounts?tab=period-end', labelKey: 'erp_data_migration', icon: Icon.erp },
          { to: '/erp-accounts?tab=reporting', labelKey: 'erp_reporting', icon: Icon.erp },
          { to: '/erp-accounts?tab=tax-compliance', labelKey: 'erp_tax', icon: Icon.erp },
          // Shown only to a tenant that runs Pharmacy: accountsChildrenFor, above.
          { to: '/fp34c', labelKey: 'fp34c', icon: Icon.erp },
          /*
           * NO "Motor Trade Reports" CHILD. Added 2026-10-13 with the move and
           * taken out the same day — Reagan: "why do we need this on the side
           * bar when its in reports".
           *
           * He is right, and it is the duplication this branch has spent its
           * time removing. The dealer's reports are a sub-tab of Reporting, and
           * NONE of the other seven Reporting sub-tabs has a sidebar row of its
           * own: Reporting is the entry and the strip is how you choose within
           * it. A second door made one sub-tab special for no reason and gave
           * it two places to go wrong.
           *
           * FP34C above is genuinely different: it is a PAGE at /fp34c, not a
           * sub-tab of anything, so without its row an accounts-only user has
           * no way to reach it at all.
           *
           * The deep link stays — /motor-trade?tab=reports still redirects to
           * #motor-trade-reports and the Accounts page still listens for it. A
           * bookmark has to land somewhere, which is a different question from
           * whether the nav needs a second row.
           */
        ],
      },
      // 2026-06-28 — collapsible parent: the 11 primary tabs drop into the
      // sidebar; each tab's sub-tab strip stays on the Payroll page.
      {
        to: '',
        labelKey: 'payroll',
        icon: Icon.payroll,
        children: [
          { to: '/payroll?tab=payroll', labelKey: 'payroll_payroll', icon: Icon.payroll },
          { to: '/payroll?tab=filing', labelKey: 'payroll_filing', icon: Icon.payroll },
          { to: '/payroll?tab=hmrc', labelKey: 'payroll_hmrc', icon: Icon.payroll },
          { to: '/payroll?tab=payments', labelKey: 'payroll_payments', icon: Icon.payroll },
          { to: '/payroll?tab=compliance', labelKey: 'payroll_compliance', icon: Icon.payroll },
          { to: '/payroll?tab=reports', labelKey: 'payroll_reports', icon: Icon.payroll },
          { to: '/payroll?tab=admin', labelKey: 'payroll_admin', icon: Icon.payroll },
          { to: '/payroll?tab=config', labelKey: 'payroll_config', icon: Icon.payroll },
        ],
      },
      {
        to: '/sub-contract',
        labelKey: 'sub_contract',
        icon: Icon.subContract,
        tenantFlag: 'subContractEnabled',
      },
      // 2026-06-28 — collapsible parent: the 4 tabs drop into the sidebar.
      // tenantFlag preserved so the whole section still gates on the add-on.
      {
        to: '',
        labelKey: 'cashflow',
        icon: Icon.cashflow,
        tenantFlag: 'cashflowEnabled',
        children: [
          { to: '/cashflow?tab=forecast', labelKey: 'cashflow_forecast', icon: Icon.cashflow },
          { to: '/cashflow?tab=scenarios', labelKey: 'cashflow_scenarios', icon: Icon.cashflow },
          { to: '/cashflow?tab=stress', labelKey: 'cashflow_stress', icon: Icon.cashflow },
          { to: '/cashflow?tab=settings', labelKey: 'cashflow_settings', icon: Icon.cashflow },
        ],
      },
      // 2026-06-28 — collapsible parent: the 4 tabs drop into the sidebar.
      // tenantFlag preserved so the whole section still gates on the add-on.
      {
        to: '',
        labelKey: 'audit_pack',
        icon: Icon.auditPack,
        tenantFlag: 'auditPackEnabled',
        children: [
          { to: '/audit-pack?tab=year_end_pack', labelKey: 'audit_yearend', icon: Icon.auditPack },
          { to: '/audit-pack?tab=working_papers', labelKey: 'audit_working_papers', icon: Icon.auditPack },
          { to: '/audit-pack?tab=tests_review', labelKey: 'audit_tests_review', icon: Icon.auditPack },
          { to: '/audit-pack?tab=accounts', labelKey: 'audit_accounts', icon: Icon.auditPack },
          { to: '/audit-pack?tab=nominal_map', labelKey: 'audit_nominal_map', icon: Icon.auditPack },
          { to: '/audit-pack?tab=templates', labelKey: 'audit_templates', icon: Icon.auditPack },
          { to: '/audit-pack?tab=evidence', labelKey: 'audit_evidence', icon: Icon.auditPack },
          { to: '/audit-pack?tab=auditor_access', labelKey: 'audit_auditor', icon: Icon.auditPack },
          { to: '/audit-pack?tab=settings', labelKey: 'audit_settings', icon: Icon.auditPack },
        ],
      },
      // Tronc lived here until 2026-08-16, beside the other paid Finance
      // add-ons. It moved to Hospitality in the Operations group — tips and
      // service charge are a hospitality concern, not a finance one.
      // 2026-06-03 — Approved Suppliers moved up to Sales & Projects
      // group per Shane.
    ],
  },
  // 2026-06-03 — Legal & Compliance group dropped per Shane. Its
  // only item (Contracts & Legal) moved up into the Administration
  // group.
  {
    titleKey: 'resources',
    items: [
      { to: '/directory', labelKey: 'directory', icon: Icon.directory },
      // 2026-06-03 — Glossary & Terminology moved up into the
      // Administration group per Shane.
    ],
  },
];

// 2026-06-28 — Sidebar-dropdowns update: the Guidance Engine standalone
// entry becomes a collapsible parent whose former in-page tabs (Reference
// Library / Contextual Panel / Step-by-Step Wizard) drop down beneath it,
// each linking to the page's `?tab=` view. Icons are unused for tab-children
// (the dropdown renders labels only) but the NavItem type requires one.
const GUIDANCE_CHILDREN: readonly NavItem[] = [
  { to: '/guidance?tab=library', labelKey: 'guidance_library', icon: Icon.glossary },
  { to: '/guidance?tab=contextual', labelKey: 'guidance_contextual', icon: Icon.glossary },
  { to: '/guidance?tab=wizard', labelKey: 'guidance_wizard', icon: Icon.glossary },
];

// 2026-06-28 — Accreditations' Auditor Portal tab only exists when the tenant
// has an active "Achieve" subscription. Appended to the Accreditations
// dropdown in the render only when that subscription is active.
/*
 * HOW-TO — 21 Sep 2026. Reagan: "can it have a drop down arrow like the rest."
 *
 * The two children are the two halves of the page, on ?tab= exactly as
 * Guidance does it: videos for "show me", FAQs for "just tell me".
 */
const HOW_TO_CHILDREN: readonly NavItem[] = [
  { to: '/how-to?tab=videos', labelKey: 'how_to_videos', icon: Icon.training },
  { to: '/how-to?tab=faqs', labelKey: 'how_to_faqs', icon: Icon.training },
];

const ACCREDITATIONS_AUDITOR_CHILD: NavItem = {
  to: '/accreditations?tab=auditor',
  labelKey: 'accreditation_auditor',
  icon: Icon.accred,
};

/**
 * 2026-06-10 — Phase 2 per-user section permissions. The full nav vocabulary
 * (labelKey + owning group) exported for the Users & Permissions editor, so
 * the checklist is derived from the SAME source the sidebar renders — one
 * vocabulary, no drift. The API validates writes against
 * SIDEBAR_SECTION_KEYS in @jeanieiq/types.
 */
export interface SidebarSectionOption {
  labelKey: string;
  groupKey: string | null;
}
export const SIDEBAR_SECTIONS: SidebarSectionOption[] = NAV_GROUPS.flatMap(
  (g) => g.items.map((i) => ({ labelKey: i.labelKey, groupKey: g.titleKey })),
);

/**
 * THE SUB-TABS, the same way (2026-10-14).
 *
 * Reagan: *"when you add a user on the tennants platfrom and you click what
 * sections of the side bar they can see we also need to add what sub side
 * bars they can see ... i may want some users to see the team section but not
 * the enquaries tab"*.
 *
 * DERIVED, NEVER TYPED OUT. There are over 150 of them across 25 sections,
 * and the one thing that must never happen is the permission checklist
 * offering a tab the sidebar does not draw, or the sidebar drawing one nobody
 * can be granted. `SIDEBAR_SECTIONS` above has been derived from NAV_GROUPS
 * since the day per-user permissions landed, for exactly this reason; this is
 * the same move one level down.
 *
 * A CHILD'S `labelKey` IS ITS PERMISSION KEY, and they are unique across the
 * whole nav — asserted in the guard test rather than assumed, because two
 * sections quietly sharing a child key would hand one tick to both.
 *
 * `to` comes along because the client router needs it: blocking a sub-tab
 * means refusing the URL, not only hiding the link, and the URL is this.
 */
export interface SidebarSubTabOption {
  /** The child's own labelKey — unique platform-wide, and the permission key. */
  key: string;
  /** The section it lives under, which must be held before this means anything. */
  sectionKey: string;
  /** Where it goes, e.g. `/sales?tab=enquiries`. */
  to: string;
}

export const SIDEBAR_SUBTABS: SidebarSubTabOption[] = NAV_GROUPS.flatMap((g) =>
  g.items.flatMap((i) =>
    (i.children ?? []).map((c) => ({ key: c.labelKey, sectionKey: i.labelKey, to: c.to })),
  ),
);

/** The sub-tabs of one section, in the order the sidebar draws them. */
export function subTabsOfSection(sectionKey: string): SidebarSubTabOption[] {
  return SIDEBAR_SUBTABS.filter((s) => s.sectionKey === sectionKey);
}

/**
 * Which sections are gated on a paid add-on, keyed by labelKey.
 *
 * Derived from NAV_GROUPS rather than typed out, so a new add-on declares
 * itself once — on its nav row — and every surface that asks "does this tenant
 * have this section" learns about it in the same change.
 */
const SECTION_TENANT_FLAG: Readonly<Record<string, TenantFeatureFlag>> = (() => {
  const map: Record<string, TenantFeatureFlag> = {};
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.tenantFlag) map[item.labelKey] = item.tenantFlag;
    }
  }
  return map;
})();

/**
 * DOES THIS TENANT HAVE THIS SECTION AT ALL? Every gate that depends on the
 * TENANT, and none that depend on the person signed in.
 *
 * ── why this is one function ───────────────────────────────────────────────
 *
 * Shane, 2026-09-26, looking at Users & Permissions offering a live tenant six
 * sections it does not run — Pharmacy, Hospitality, Motor Trade, Hire,
 * Lettings, Standard Sales: *"what is on the side bar should be in the
 * permissions selection that the director then chooses what area of the
 * company sees what"*, and *"basically what is selected in the JEanieIQ admin
 * section is what feeds to the tenents sidebar"*.
 *
 * The two screens already shared a VOCABULARY — SIDEBAR_SECTIONS — and that is
 * exactly what made the drift invisible: the permission editor looped the same
 * list and simply never asked any of the questions the sidebar asks
 * before drawing a row. A shared constant looks like consolidation and is not;
 * only a shared rule is. So the sidebar and the editor now call this, and a
 * fifth gate added here reaches both without anyone remembering.
 *
 * ── what is deliberately NOT here ──────────────────────────────────────────
 *
 * The three per-USER gates: the granted allow-list, the transport_manager
 * fleet scope, and an item's own `roles`. Those answer "may this person open
 * it", which is the question the permissions editor exists to ANSWER — folding
 * them in would hide from a director the very rows they are there to tick.
 *
 * Exported and pure: it decides what a paying customer can reach.
 */
export function tenantOffersSection(
  labelKey: string,
  tenantProfile: TenantProfile | undefined,
  nation: Nation,
): boolean {
  // Not loaded yet is a no: an empty list reads as holding every section, so
  // answering from nothing shows a company sections that are not its own.
  if (!tenantProfile) return false;
  // The Platform Admin selection - see tenantHasSection for the show/deny rule.
  if (
    !tenantHasSection(
      labelKey,
      tenantProfile.selectedSections ?? [],
      tenantProfile.hiddenSections ?? [],
    )
  ) {
    return false;
  }
  // 2026-08-24 nation split - a module that does not exist in this branch's
  // nation is not shown. Unlisted modules are UK-wide; the API enforces the
  // same map via middleware/nationRegistry.
  if (!moduleVisibleInNation(labelKey, nation)) return false;
  // 2026-05-13 - paid add-on modules. When the tenant hasn't bought in (flag
  // is false / undefined), the entry hides.
  const flag = SECTION_TENANT_FLAG[labelKey];
  if (flag && tenantProfile[flag] !== true) return false;
  return true;
}

/* ── The header breadcrumb, derived from this same nav model ────────────────
 *
 * Shane, 2026-08-20: *"we are going round in circles.. it is all mixed... the
 * best way is the parent name on the top with child name please"*.
 *
 * Every page used to hand the Header its own hand-typed crumb. Ninety-nine of
 * them, written at different times by different hands, so the header said
 * "Awareness Course Library" while the sidebar entry you clicked said
 * "Awareness Courses" — and on some tabs it named a different tab entirely,
 * because the crumb was computed from a stale ternary the tab list had since
 * outgrown. Chasing those page by page is what went round in circles: fixing
 * ninety-nine copies never makes them one thing.
 *
 * So the trail is RESOLVED FROM THE NAV MODEL. The header shows exactly the
 * path you clicked to get there — group, then parent, then child — using the
 * sidebar's own label keys. A sidebar rename now moves the header with it,
 * and the two cannot drift apart again because there is only one list.
 */

const navChildPath = (to: string): string => {
  const q = to.indexOf('?');
  return q === -1 ? to : to.slice(0, q);
};
const navChildParams = (to: string): URLSearchParams => {
  const q = to.indexOf('?');
  return new URLSearchParams(q === -1 ? '' : to.slice(q + 1));
};
/** Does the current URL carry every query param this entry pins? */
const navParamsMatch = (entry: NavItem, current: URLSearchParams): boolean => {
  for (const [key, value] of navChildParams(entry.to)) {
    if (current.get(key) !== value) return false;
  }
  return true;
};

/**
 * Is this nav entry the current page? Shared by the sidebar highlight and the
 * header trail so a route can never be "active" in one and not the other.
 *
 * Matching is on path plus EVERY query param the entry pins, not on `tab`
 * alone. Several sections deliberately share a path and separate on their own
 * parameter — Vehicles maintenance and the Maintenance Schedule both live at
 * /maintenance-schedule and differ by `?type=` — and a tab-only matcher lights
 * up, and breadcrumbs, the wrong one of the pair.
 *
 * `siblings` supplies the section's default entry (its first) so the bare
 * `/training` URL resolves to the first tab rather than to nothing, and lets an
 * unparameterised entry stand aside when a sibling claims the current query.
 *
 * The empty-path guard matters here and not in the sidebar: collapsible
 * parents carry `to: ''`, and `''` is a prefix of every path.
 */
/*
 * One reading of activePrefix, used by the breadcrumb trail and the row
 * highlight. They had the same startsWith inline in two places, which is how
 * a list would have been supported in one and not the other.
 */
export function matchesActivePrefix(
  prefix: string | string[] | undefined,
  pathname: string,
): boolean {
  if (!prefix) return false;
  const list = Array.isArray(prefix) ? prefix : [prefix];
  return list.some((p) => p.length > 0 && pathname.startsWith(p));
}

export function navEntryActive(
  entry: NavItem,
  siblings: readonly NavItem[],
  pathname: string,
  search: string,
  /**
   * Sub-tab keys this person cannot reach, used ONLY by the default-entry
   * rule below. Optional, and an absent set means nothing is blocked —
   * which is right for a director and for every caller that is drawing the
   * nav itself, where the blocked rows have already been filtered out.
   */
  blocked?: ReadonlySet<string>,
): boolean {
  const ePath = navChildPath(entry.to);
  if (!ePath) return false;
  const current = new URLSearchParams(search);
  const pinned = navChildParams(entry.to);

  /*
   * Rows that own more than their own path — see NavItem.activeWhenIn.
   * Checked first, and still yielding to an exact owner.
   */
  if (entry.activeWhenIn && entry.activeWhenIn.length > 0) {
    const inside = entry.activeWhenIn.some(
      (p) => pathname === p || pathname.startsWith(`${p}/`),
    );
    if (inside) {
      const claimedByAnother = allNavEntries().some(
        (other) => other !== entry && navChildPath(other.to) === pathname,
      );
      if (!claimedByAnother) return true;
    }
  }

  /*
   * An entry can claim other tabs on its own path (activeTabs) — the in-page
   * tabs of the screen it points at.
   *
   * Checked before the pinned-params logic, but it still yields to any entry
   * that pins that exact tab: if a tab ever gets its own sidebar row, that row
   * wins and this one stops claiming it. So adding a row later needs no change
   * here.
   */
  if (entry.activeTabs && entry.activeTabs.length > 0 && pathname === ePath) {
    const tab = current.get('tab');
    if (tab && entry.activeTabs.includes(tab)) {
      const claimedByAnother = allNavEntries().some(
        (other) =>
          other !== entry &&
          navChildPath(other.to) === ePath &&
          navChildParams(other.to).get('tab') === tab,
      );
      if (!claimedByAnother) return true;
    }
  }

  /*
   * 2026-08-25 — claimsUnpinnedTabs. See the field's docstring on NavItem.
   *
   * Same shape as activeTabs above, with membership inverted: instead of "is
   * this tab on my list", it asks "does this tab belong to nobody else". That
   * is the only form the question can take when the tab ids are per-tenant
   * data — Stock's brand catalogues — rather than something a developer wrote.
   *
   * Runs AFTER activeTabs so an explicit claim always wins, and returns false
   * for a bare path (no ?tab=) so it never competes with the default-entry rule
   * further down.
   */
  if (entry.claimsUnpinnedTabs && pathname === ePath) {
    const tab = current.get('tab');
    if (tab) {
      const spokenFor = allNavEntries().some((other) => {
        if (navChildPath(other.to) !== ePath) return false;
        if (navChildParams(other.to).get('tab') === tab) return true;
        return other !== entry && (other.activeTabs?.includes(tab) ?? false);
      });
      if (!spokenFor) return true;
    }
  }

  if (Array.from(pinned).length > 0) {
    if (pathname !== ePath) return false;
    if (navParamsMatch(entry, current)) {
      /*
       * MOST SPECIFIC WINS. Two entries can both match the same URL when one
       * pins a subset of the other's params: Enquiries' first tab pins
       * `?tab=enq`, and Switchboard Builder's Sales entry pins
       * `?tab=enq&build=switchboard`. Both are satisfied by the switchboard
       * URL, and the earlier one in NAV_GROUPS was winning - so Sales lit up
       * as Enquiries and the section never opened.
       *
       * Routers resolve this by specificity, and so does this: an entry yields
       * to another on the same path that also matches and pins strictly more.
       */
      const mine = Array.from(pinned).length;
      return !allNavEntries().some((other) => {
        if (other === entry || navChildPath(other.to) !== ePath) return false;
        if (Array.from(navChildParams(other.to)).length <= mine) return false;
        return navParamsMatch(other, current);
      });
    }
    /*
     * The default entry also answers for the bare URL that pins nothing - but
     * not when another entry claims the current query outright. Without that
     * second half, Jobs' first tab answered for /jobs?build=switchboard
     * because `build` is not one of the keys it pins.
     */
    /*
     * THE FIRST SIBLING ANSWERS FOR A BARE URL — the first one this person
     * can actually reach (2026-10-14, found in the audit).
     *
     * It used to be `siblings[0]` flat. That was true for as long as the
     * first tab was always reachable, and sub-tab blocking ended it: a
     * manager blocked from Enquiries opened /sales, the page landed them on
     * Quotations — correctly — and the breadcrumb above it read
     * "Operations › Sales › Enquiries". The screen was right and the
     * heading over it named a tab they are not allowed on.
     *
     * Falls back to siblings[0] when every sibling is blocked, which is a
     * person who should not be seeing the section at all. Naming the first
     * one is no worse than naming nothing, and the section gate is the
     * thing that should have stopped them.
     */
    const firstReachable =
      siblings.find((sib) => !blocked?.has(sib.labelKey)) ?? siblings[0];
    if (firstReachable !== entry) return false;
    if (!Array.from(pinned).every(([key]) => !current.has(key))) return false;
    return !allNavEntries().some(
      (other) =>
        other !== entry &&
        navChildPath(other.to) === ePath &&
        Array.from(navChildParams(other.to)).length > 0 &&
        navParamsMatch(other, current),
    );
  }

  if (pathname !== ePath && !pathname.startsWith(`${ePath}/`)) return false;
  /*
   * An entry that pins nothing yields to ANY entry on the same path that
   * matches the current query outright - not just a sibling.
   *
   * It used to check siblings only, which covered the pairs that share a
   * parent (Vehicles maintenance and the Maintenance Schedule, both on
   * /maintenance-schedule). Switchboard Builder broke that assumption on
   * 2026-08-22: its Sales and Engineering entries point at /enquiries and
   * /jobs with ?build=switchboard, under a DIFFERENT parent from the plain
   * sections on the same paths. The unparameterised entry won every time and
   * /jobs?build=switchboard trailed under Jobs & Projects.
   *
   * Caught by nav-trail.test.ts, which walks every destination and asserts the
   * trail ends on that entry's own label.
   */
  return !allNavEntries().some(
    (other) =>
      other !== entry &&
      navChildPath(other.to) === ePath &&
      Array.from(navChildParams(other.to)).length > 0 &&
      navParamsMatch(other, current),
  );
}

/** Every nav entry, parents and children, flattened once. */
let NAV_ENTRIES_FLAT: NavItem[] | null = null;
function allNavEntries(): readonly NavItem[] {
  if (NAV_ENTRIES_FLAT) return NAV_ENTRIES_FLAT;
  const out: NavItem[] = [];
  const walk = (items: readonly NavItem[]): void => {
    for (const item of items) {
      out.push(item);
      if (item.children) walk(item.children);
    }
  };
  for (const group of NAV_GROUPS) walk(group.items);
  NAV_ENTRIES_FLAT = out;
  return out;
}

/** The children the sidebar actually renders, including the injected ones. */
function navEntryChildren(item: NavItem): readonly NavItem[] | undefined {
  if (item.labelKey === 'accreditations') {
    return [...(item.children ?? []), ACCREDITATIONS_AUDITOR_CHILD];
  }
  return item.children;
}

/**
 * Guidance sits outside NAV_GROUPS — it is pinned above Admin and its label
 * lives at `sidebar.guidance_engine`, not under `sidebar.nav`. Modelled here
 * so the trail covers it too.
 */
const GUIDANCE_PARENT_KEY = 'sidebar.guidance_engine';
const HOW_TO_PARENT_KEY = 'sidebar.how_to';

/**
 * The nav model, for the breadcrumb audit in `__tests__/nav-trail.test.ts`.
 * Exported read-only: the test walks every destination and asserts the header
 * trail ends on that entry's own label, so a new section cannot be added with
 * a header that resolves to nothing.
 */
export const NAV_GROUPS_FOR_TEST: readonly NavGroup[] = NAV_GROUPS;

export interface NavTrail {
  /** Full `common` translation keys, outermost first. */
  keys: string[];
  /**
   * True when the location IS this nav entry. False when it only sits under
   * the entry's `activePrefix` — a sub-page the nav does not model, such as
   * /admin/documents/:id under the Admin hub.
   *
   * The distinction matters to the header: on an exact match the page's own
   * first crumb is its guess at its section name and the nav model overrules
   * it, but on a prefix match the page's crumbs are the only thing that knows
   * where you actually are. Dropping the first one there cost the Document
   * Register its name and left "Administration > Admin > DOC-0001".
   */
  exact: boolean;
}

/** A sidebar's entries: the fixed model, or what was drawn for one person. */
export interface NavMenu {
  groups: readonly NavGroup[];
  guidance: readonly NavItem[];
  howTo: readonly NavItem[];
  childrenOf: (item: NavItem) => readonly NavItem[] | undefined;
}

function fixedMenu(): NavMenu {
  return { groups: NAV_GROUPS, guidance: GUIDANCE_CHILDREN, howTo: HOW_TO_CHILDREN, childrenOf: navEntryChildren };
}

/** The fixed model cut down to the entries at these addresses, as a sidebar that drew them would hold it. */
export function menuOfDrawnAddresses(tos: ReadonlySet<string>): NavMenu {
  const keep = (items: readonly NavItem[]): NavItem[] =>
    items.flatMap((item) => {
      const kids = item.children ? keep(item.children) : undefined;
      if (tos.has(item.to) || (kids && kids.length > 0)) return [kids ? { ...item, children: kids } : item];
      return [];
    });
  return {
    groups: NAV_GROUPS.map((g) => ({ ...g, items: keep(g.items) })),
    guidance: keep(GUIDANCE_CHILDREN),
    howTo: keep(HOW_TO_CHILDREN),
    childrenOf: (item) => item.children,
  };
}

/**
 * The breadcrumb for a location. Null when the route is not in the sidebar at
 * all (a record detail page reached from a list) — the caller then keeps the
 * page's own crumbs untouched.
 */
function resolveNav(
  pathname: string,
  search: string,
  blocked?: ReadonlySet<string>,
  menu: NavMenu = fixedMenu(),
): (NavTrail & { entry: NavItem }) | null {
  // Tab children first, across every group. A `?tab=` child must beat a plain
  // item that happens to share its path, or two sections living on one path
  // (Fleet's equipment register and Plant both sit on /plant) would resolve to
  // whichever came first in the list rather than to the tab you are on.
  for (const child of menu.guidance) {
    if (navEntryActive(child, menu.guidance, pathname, search, blocked)) {
      return { keys: [GUIDANCE_PARENT_KEY, `sidebar.nav.${child.labelKey}`], exact: true, entry: child };
    }
  }
  for (const child of menu.howTo) {
    if (navEntryActive(child, menu.howTo, pathname, search, blocked)) {
      return { keys: [HOW_TO_PARENT_KEY, `sidebar.nav.${child.labelKey}`], exact: true, entry: child };
    }
  }
  /*
   * GRANDCHILDREN FIRST (2026-08-22). Switchboard Builder nests two levels:
   * Switchboard Builder > Sales > Enquiries. Matching children before
   * grandchildren stopped the trail at "Sales" and never named the page, and a
   * branch parent points at its own first leaf so it always matched first.
   * Deepest wins, so the crumb reads the whole way down.
   */
  for (const group of menu.groups) {
    for (const item of group.items) {
      for (const child of menu.childrenOf(item) ?? []) {
        const grandkids = child.children ?? [];
        for (const g of grandkids) {
          if (navEntryActive(g, grandkids, pathname, search, blocked)) {
            return {
              keys: [
                ...(group.titleKey ? [`sidebar.groups.${group.titleKey}`] : []),
                `sidebar.nav.${item.labelKey}`,
                `sidebar.nav.${child.labelKey}`,
                `sidebar.nav.${g.labelKey}`,
              ],
              exact: true,
              entry: g,
            };
          }
        }
      }
    }
  }
  for (const group of menu.groups) {
    for (const item of group.items) {
      const kids = menu.childrenOf(item);
      if (!kids?.length) continue;
      for (const child of kids) {
        if (navEntryActive(child, kids, pathname, search, blocked)) {
          return {
            keys: [
              ...(group.titleKey ? [`sidebar.groups.${group.titleKey}`] : []),
              `sidebar.nav.${item.labelKey}`,
              `sidebar.nav.${child.labelKey}`,
            ],
            exact: true,
            entry: child,
          };
        }
      }
    }
  }
  // Then standalone entries.
  for (const group of menu.groups) {
    for (const item of group.items) {
      if (menu.childrenOf(item)?.length) continue;
      // Siblings are the whole group: entries that share a path and separate
      // on a query param (Vehicles maintenance vs Maintenance Schedule) are
      // group items, not tab children, and each has to yield to the other.
      if (navEntryActive(item, group.items, pathname, search, blocked)) {
        return {
          keys: [
            ...(group.titleKey ? [`sidebar.groups.${group.titleKey}`] : []),
            `sidebar.nav.${item.labelKey}`,
          ],
          exact: true,
          entry: item,
        };
      }
    }
  }
  // Last, the declared sub-page prefixes (Admin's /admin/* pages).
  for (const group of menu.groups) {
    for (const item of group.items) {
      if (matchesActivePrefix(item.activePrefix, pathname)) {
        return {
          keys: [
            ...(group.titleKey ? [`sidebar.groups.${group.titleKey}`] : []),
            `sidebar.nav.${item.labelKey}`,
          ],
          exact: false,
          entry: item,
        };
      }
    }
  }
  return null;
}

export function resolveNavTrail(
  pathname: string,
  search: string,
  /** What this person cannot reach — see navEntryActive. */
  blocked?: ReadonlySet<string>,
): NavTrail | null {
  const found = resolveNav(pathname, search, blocked);
  return found ? { keys: found.keys, exact: found.exact } : null;
}

/**
 * The nav entry a page is, by the same walk as the breadcrumb, or null when none
 * is. A page claimed only by a prefix (My Profile, the /admin/* pages) has no
 * entry of its own, so it is null here: the prefix is for the breadcrumb.
 */
export function resolveNavEntry(pathname: string, search: string): NavItem | null {
  const found = resolveNav(pathname, search);
  return found?.exact ? found.entry : null;
}

/** The same, among the entries a sidebar drew. */
export function resolveDrawnEntry(menu: NavMenu, pathname: string, search: string): NavItem | null {
  const found = resolveNav(pathname, search, undefined, menu);
  return found?.exact ? found.entry : null;
}

export function Sidebar(): JSX.Element {
  const { t, i18n } = useTranslation('common', { bindI18n: 'languageChanged loaded' });
  const { pathname, search } = useLocation();

  // Per-tenant feature visibility (2026-05-06 per Shane). Each tenant
  // can hide individual sidebar sections via tenant.hiddenSections —
  // e.g. One tenant hides care/hire/lettings, JeanieIQ also hides plant /
  // building-maintenance / stock. Empty array (default) shows all.
  // useCurrentTenant is cached — sidebar mounts once per session.
  const tenantQuery = useCurrentTenant();
  /*
   * What a tenant HAS is decided by `tenantOffersSection` above - the show
   * list, the nation, the sector and the add-on flag, in one place, because
   * Users & Permissions has to ask the identical question. The two lists this
   * used to unpack by hand (hiddenSections, selectedSections) are read inside
   * that rule now, so the sidebar and the permissions editor cannot answer it
   * differently. The show-list changeover is documented on `tenantHasSection`.
   */
  // The active branch's nation, for the devolved-module filter below.
  const { nation } = useBranch();

  // 2026-06-10 — Phase 2: per-user ALLOW-list, default OFF (Shane / GDPR
  // data-minimisation). A non-director sees ONLY the sections a director has
  // ticked on in Users & Permissions; directors are exempt (full nav). Same
  // labelKey vocabulary as hiddenSections; the API enforces the same boundary
  // in verifyTenant (403 SECTION_DENIED), so this is the matching UX gate,
  // not the security.
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchMe(signal),
  });
  const allowedSections = new Set(meQuery.data?.allowedSections ?? []);
  /*
   * 2026-10-14 — the same gate one level down. Reagan: *"i may want some
   * users to see the team section but not the enquaries tab"*.
   *
   * A DENY list, so an empty one is every sub-tab of the sections above —
   * which is what everybody has, and what a tab shipped next month is until
   * somebody turns it off. Unlike `allowedSections`, which is an allow-list
   * and defaults to nothing.
   */
  const blockedSubTabs = new Set(meQuery.data?.blockedSubtabs ?? []);

  // Stage 1.2e (2026-05-10) — role-based filter. Items with a `roles`
  // array only render for matching users; items without a `roles`
  // array are visible to everyone authenticated (the default). Pairs
  // with the `requireRole` middleware on the corresponding API routes.
  const currentRole = getCurrentUserRole();

  // 2026-06-28 — collapsible sidebar parents are ROUTE-DRIVEN: a section is
  // open when the current route is inside it (one of its children is active).
  // Clicking a parent navigates to its first child — that opens the section
  // and, because expansion follows the route, collapses every other section
  // (accordion: only the section you're in is open).
  const navigate = useNavigate();

  // 2026-06-28 — Fleet is a genuinely tiered section: Plus adds tabs over Base
  // (highest enabled tier wins, so Plus overrides Base). The sidebar shows the
  // tab set the tenant's effective Fleet tier entitles them to. Transport
  // Manager is a Fleet-scoped role that also gets the My Credentials child.
  const fleetIsPlus = useSectionTiers('fleet_transport').effectiveTier !== 'base';
  const isTransportManager = currentRole === 'transport_manager';
  // Stock Management: Usage + Barcode need the Customised level (the scanning add-on).
  const stockTier = useSectionTiers('stock_management').effectiveTier;

  // Is this child the CURRENT page? For `?tab=` children the match is
  // path + tab (not path alone) — otherwise two sections that share a path
  // (e.g. Fleet's moved Equipment Register also lives at /plant) would both
  // count as active/open. `siblings` supplies the section's default tab (its
  // first child) for the no-`?tab=` URL. (Shane, 2026-07-23.)
  // One matcher, shared with resolveNavTrail (above), so the highlighted
  // sidebar row and the header breadcrumb always name the same entry.
  const childActive = (child: NavItem, siblings: readonly NavItem[]): boolean =>
    navEntryActive(child, siblings, pathname, search);
  /*
   * A section is open when the route is anywhere INSIDE it - including inside
   * a nested branch.
   *
   * It used to look only at direct children. Switchboard Builder's children
   * are Sales and Engineering, which are themselves parents, so clicking any
   * leaf below them (Quotations, Production Prep...) left no direct child
   * matching and the whole section snapped shut - you had to reopen it to get
   * anywhere else. Shane, 2026-08-22: *"when the child is being clicked it
   * closes the sidebar and you have to reopen when you want to go to another
   * area"*.
   */
  /*
   * A DYNAMIC TAB MUST NOT CLOSE THE SECTION (2026-08-25).
   *
   * Reagan: "when clicking on the catogy when catogories have been made by the
   * customer it colapses the side bar ... it should just stay with the side bar
   * open and no jumps."
   *
   * Stock's brand and category strips push `?tab=<uuid>` (see selectBrand in
   * ProStockView). Those ids are created by the CUSTOMER, so no sidebar child
   * can ever pin them — every child failed to match and Stock Management
   * snapped shut on the very click that was navigating inside it. Measured on
   * the running app: /stock?tab=summary gave aria-expanded="true" with 7
   * children; /stock?tab=<a-uuid> gave "false" with 0.
   *
   * Same symptom as the Switchboard Builder fault above, different cause: that
   * one was nesting, this one is ids that cannot be enumerated in advance.
   *
   * The fallback is deliberately last-resort — it only applies when NOTHING
   * anywhere matched the URL outright, so every already-working route keeps
   * resolving exactly as before and the path+tab precedence that separates
   * sections sharing a path (Fleet's Equipment Register and Plant, both on
   * /plant) is untouched. In the leftover case — a path shared by two sections
   * AND an unrecognised tab — both open. Two open sections beats the section
   * you are standing in slamming shut.
   *
   * Deliberately NOT fixed inside navEntryActive: that matcher also drives the
   * header breadcrumb and the highlighted row, and is covered by
   * nav-trail.test.ts. Widening it would change what the trail NAMES. This
   * only changes what is OPEN.
   */
  const nothingMatchedExactly = !allNavEntries().some((e) => {
    const parent = allNavEntries().find((x) => x.children?.includes(e));
    return navEntryActive(e, parent?.children ?? [e], pathname, search);
  });
  const ownsCurrentPath = (children: readonly NavItem[]): boolean =>
    children.some((c) => {
      const cp = navChildPath(c.to);
      return !!cp && (pathname === cp || pathname.startsWith(`${cp}/`));
    });

  const isSectionOpen = (children: readonly NavItem[]): boolean =>
    children.some(
      (c) =>
        childActive(c, children) ||
        ((c.children?.length ?? 0) > 0 && isSectionOpen(c.children ?? [])),
    ) || (nothingMatchedExactly && ownsCurrentPath(children));

  // 2026-06-28 — accordion toggle (Shane). A section auto-opens for the route
  // it's on (isSectionOpen). Clicking the OPEN section's parent collapses it
  // WITHOUT navigating — so you can browse the sidebar and pick another without
  // the page jumping. Any navigation (clicking a child, or a closed parent)
  // clears forceClosed and route-driven open resumes.
  const [forceClosed, setForceClosed] = useState(false);
  useEffect(() => {
    setForceClosed(false);
  }, [pathname, search]);

  /*
   * The same accordion rule, one level down (2026-09-21).
   *
   * Shane: *"can you make the tabs collaps when clicked as well as open"*. A
   * NESTED parent — ISO 9001 with its twelve areas, Sales with its leaves —
   * opened when the route was inside it and could not be closed again, so a
   * long branch stayed open underneath whatever you were reading.
   *
   * A SET keyed on labelKey rather than the single `forceClosed` flag above:
   * that flag means "the section the route is in is collapsed", and there is
   * only ever one of those. Branches nest and there can be several open at
   * once, so collapsing ISO 9001 must not also collapse Accreditations around
   * it.
   *
   * Cleared on navigation, the same rhythm as forceClosed, so route-driven open
   * resumes when you come back to the branch from somewhere else.
   */
  const [collapsedBranches, setCollapsedBranches] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  useEffect(() => {
    setCollapsedBranches(new Set<string>());
  }, [pathname, search]);

  const toggleBranch = (labelKey: string, collapse: boolean): void => {
    setCollapsedBranches((prev) => {
      const next = new Set(prev);
      if (collapse) next.add(labelKey);
      else next.delete(labelKey);
      return next;
    });
  };
  const sectionExpanded = (children: readonly NavItem[]): boolean =>
    isSectionOpen(children) && !forceClosed;
  // Toggle a collapsible parent: collapse if open (no nav), else open + go to
  // its first child page.
  const toggleSection = (children: readonly NavItem[]): void => {
    if (sectionExpanded(children)) {
      setForceClosed(true);
      return;
    }
    setForceClosed(false);
    const first = children[0];
    if (first) navigate(first.to);
  };

  // 2026-06-28 — render a collapsible parent's children. Children may be
  // distinct routes (Pharmacy modules) OR in-page tabs of one route
  // (`/guidance?tab=library`). NavLink's isActive is path-only, so for
  // `?tab=` children we compute active from pathname + the ?tab= param (the
  // first child is active when the URL carries no ?tab=, i.e. the default tab).
  const renderTabChildren = (children: readonly NavItem[], depth = 0): JSX.Element[] =>
    children.flatMap((child) => {
      /*
       * A THIRD LEVEL (2026-08-22). Switchboard Builder needs it: Sales and
       * Engineering are branches of one flow, each with its own screens, and
       * flattening all eleven under one heading loses the split that makes the
       * section readable.
       *
       * A nested parent renders as its own row with its leaves one indent
       * further in, and opens on the same rule as a top-level section - when
       * the route is inside it - so it is open on arrival rather than needing a
       * second click.
       */
      const kids = child.children ?? [];
      if (kids.length > 0) {
        /*
         * Open when the route is ON the branch or anywhere INSIDE it.
         *
         * Checking only the leaves was not enough once branches got their own
         * URL: Sales points at ?build=switchboard with no ?tab=, so no leaf
         * matched it and clicking Sales expanded nothing. Shane, 2026-08-23:
         * *"sales and engineering children are not opening again"*.
         */
        /*
         * `routeOpen` is where the route says it should be; `open` is that
         * minus what the person has collapsed by hand. Kept apart because the
         * click handler needs both: clicking a branch the route is inside must
         * re-open it rather than navigate to the page it is already on.
         */
        const routeOpen = childActive(child, children) || isSectionOpen(kids);
        const open = routeOpen && !collapsedBranches.has(child.labelKey);
        return [
          <NavLink
            /*
             * Keyed on labelKey, not `to`. A branch parent deliberately points
             * at its own first leaf - Sales and Enquiries are both
             * `?tab=enq&build=switchboard` - so keying on the URL gave two rows
             * the same key and React reused elements across slots: Engineering
             * rendered twice, once inside Sales. Shane, 2026-08-22.
             */
            key={`${child.labelKey}-branch`}
            to={child.to}
            className={() => `ni${open ? ' active' : ''}`}
            style={{ paddingLeft: 34 + depth * 14 }}
            data-testid={`nav-branch-${child.labelKey}`}
            aria-expanded={open}
            /*
             * Three cases, and only the last one navigates:
             *   open            -> collapse it, stay where you are
             *   collapsed here  -> re-open it, stay where you are
             *   route elsewhere -> let the link go, and the route change clears
             *                      the collapsed set so it opens on arrival
             *
             * The first two preventDefault for the same reason the top-level
             * accordion does: you are browsing the sidebar, not asking to move,
             * and a page that jumps under you while you look for something is
             * the behaviour that rule was written to stop.
             */
            onClick={(e) => {
              const action = branchClickAction({
                routeOpen,
                collapsed: collapsedBranches.has(child.labelKey),
              });
              if (action === 'navigate') return;
              e.preventDefault();
              toggleBranch(child.labelKey, action === 'collapse');
            }}
          >
            <span className="ni-label">{t(`sidebar.nav.${child.displayLabelKey ?? moduleLabelKey(child.labelKey, nation)}`)}</span>
          </NavLink>,
          ...(open ? renderTabChildren(kids, depth + 1) : []),
        ];
      }
      return [renderLeaf(child, children, depth)];
    });

  const renderLeaf = (
    child: NavItem,
    children: readonly NavItem[],
    depth: number,
  ): JSX.Element => {
      // Precise per-tab active (path + tab). Use NavLink's CALLBACK className
      // form so React Router doesn't also append its own path-only "active" —
      // that path-only match is what lit up every sibling sharing the path
      // and left the moved /plant register out. (Shane, 2026-07-23.)
    const active = childActive(child, children);
    return (
      <NavLink
        key={child.labelKey}
        to={child.to}
        className={() => `ni ni-sub${active ? ' active' : ''}`}
        // No fontSize here: the row already carries .ni (12.5px, var(--ff)).
        // The old inline 13px made every tab child LARGER than the section it
        // sits under, which is why the sidebar read as mixed sizes.
        style={{ paddingLeft: 34 + depth * 14 }}
      >
        {/* Through moduleLabelKey: a module both nations have can go by a
            different name in one of them (the monthly claim check in Wales). */}
        <span className="ni-label">{t(`sidebar.nav.${child.displayLabelKey ?? moduleLabelKey(child.labelKey, nation)}`)}</span>
        {/*
          A SUB-TAB CAN CARRY A BADGE NOW (2026-10-14). Until this line every
          badge hung off a top-level section, so the only way to say "three
          companies are waiting" was to put the number on the section and let
          the reader guess which of its five tabs it meant.
        */}
        {renderItemBadge(child.labelKey)}
      </NavLink>
    );
  };

  // Filter NAV_GROUPS once per render. Drop items this tenant does not have,
  // drop items whose role gate fails, drop empty groups.
  const tenantProfile = tenantQuery.data;
  /*
   * `profileSectors` WAS HERE, and it is gone (2026-09-30).
   *
   * It read the tenant's effective operating sectors for one caller: the Rota
   * children rule, which asked `profileSectors.includes('pharmacy')`. That rule
   * now asks whether the tenant HOLDS the pharmacy section instead, because the
   * API it sends people to is gated on the section and the sidebar must not
   * hand out a link that can only 403.
   *
   * Removing it left no other reader, so the sector decided nothing in this
   * sidebar at all — the whole nav is driven by the section show/deny list.
   * Worth knowing before adding "sector-flavoured" nav: there is no precedent
   * here to follow, and Shane's rule from the accreditations work says why —
   * a sector describes a trade, not a tenant.
   */
  // 2026-06-28 — gates the Rota "Shift Management" sidebar child (default true;
  // explicitly false for tenants that don't do shift work, e.g. A live tenant).
  const shiftManagementEnabled = tenantProfile?.shiftManagementEnabled !== false;
  /*
   * "OVERRIDE ALL SHIFT patterns on" (2026-08-30) - the platform testing switch.
   *
   * Shane: *"he cannot stop everyone from seeing things in testing"*. One
   * tenant's configuration was deciding what a tester could look at, and a
   * tester signed in as that tenant had no way round it.
   *
   * Fail CLOSED on an unloaded profile (=== true, not !== false): a switch whose
   * job is to reveal hidden entries must not reveal them during a fetch, or
   * every tenant flashes the full shift set on load.
   */
  const overrideAllShiftPatterns = tenantProfile?.overrideAllShiftPatterns === true;
  // No sections until the company has loaded: with no list the rule below reads
  // as holding every section, which would show Pharmacy to a switchboard maker.
  const visibleNavGroups: NavGroup[] = (tenantProfile ? NAV_GROUPS : []).map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      // Every TENANT-level gate - the show list, the nation, the sector and
      // the paid add-on flag. Shared with the Users & Permissions checklist so
      // a director is only offered the sections this tenant actually runs.
      if (!tenantOffersSection(item.labelKey, tenantProfile, nation)) return false;
      /*
       * THE DASHBOARD IS EVERYONE'S — Reagan, 2026-09-23. It is not a section a
       * director grants; every user has one, and what appears ON it follows the
       * sections they can see. Filtering the nav item by allowed_sections hid
       * the platform's own landing page from anybody with no grants yet.
       *
       * BELOW the tenant gate and ABOVE the per-user one, which is where it sat
       * before main folded the tenant checks into tenantOffersSection. A tenant
       * that does not run the dashboard still does not get it; a PERSON with no
       * grants yet does.
       */
      if (item.labelKey === 'dashboard') return true;
      if (currentRole !== 'director' && !allowedSections.has(item.labelKey)) {
        return false;
      }
      // 2026-06-06 (PM) — transport_manager is a Fleet-scoped role: it
      // sees the Fleet & Transport section ONLY, nothing else in the nav.
      // The API enforces the same boundary (requireRole on Fleet routes;
      // every other section's routes exclude this role), so this is the
      // matching UX gate. Items without a `roles` array would otherwise
      // show to everyone, so this explicit allowlist is required.
      if (
        currentRole === 'transport_manager' &&
        item.labelKey !== 'fleet_transport'
      ) {
        return false;
      }
      if (item.roles && (currentRole === null || !item.roles.includes(currentRole))) {
        return false;
      }
      return true;
    })
      /*
       * SUB-TABS THE PERSON HAS BEEN BLOCKED FROM (2026-10-14).
       *
       * Reagan: *"i may want some users to see the team section but not the
       * enquaries tab ... block it not just hide"*. This is the hiding half;
       * the router refuses the URL and `verifyTenant` refuses the request for
       * the routes that have declared their tabs.
       *
       * A PARENT WITH NOTHING LEFT UNDER IT GOES TOO, but only when it is a
       * pure container — `to: ''`, a heading that exists to be expanded.
       * Dropping a parent that is itself a page would take away a screen the
       * person still holds.
       *
       * Directors are exempt here as everywhere else in this filter.
       */
      .map((item) => {
        if (currentRole === 'director' || !item.children || blockedSubTabs.size === 0) {
          return item;
        }
        const kids = item.children.filter((c) => !blockedSubTabs.has(c.labelKey));
        if (kids.length === item.children.length) return item;
        if (kids.length === 0 && item.to === '') return null;
        return { ...item, children: kids };
      })
      .filter((item): item is NavItem => item !== null),
  })).filter((group) => group.items.length > 0);

  // A badge is fetched only for an entry drawn for this person: the company holds
  // the area and the person is ticked for it.
  const drawnLabelKeys = new Set(visibleNavGroups.flatMap((g) => g.items.map((i) => i.labelKey)));
  const draws = (labelKey: string): boolean =>
    Boolean(tenantQuery.data && meQuery.data) && drawnLabelKeys.has(labelKey);

  // Session 3.10 — Approved Suppliers amber count badge (overdue reviews).
  // Refetches every 60s so the sidebar reflects newly-approved or
  // newly-overdue suppliers without a hard reload.
  const supplierBadgeQ = useQuery({
    queryKey: ['sidebar', 'supplier-badge'],
    queryFn: ({ signal }) => fetchSupplierBadge(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('approved_suppliers'),
  });
  const supplierBadgeCount = supplierBadgeQ.data ?? 0;

  // Session 3.8 — Holiday amber pending-count badge.
  const holidayBadgeQ = useQuery({
    queryKey: ['sidebar', 'holiday-badge'],
    queryFn: ({ signal }) => fetchHolidayBadge(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('holiday'),
  });
  const holidayBadgeCount = holidayBadgeQ.data ?? 0;

  /*
   * Session 3.11 — the red unassigned-enquiry badge, ONCE PER SECTION.
   *
   * Reagan, 2026-10-15: *"can we find out why if you put something on the
   * front desk its feeding the standard sales and jobs? this just needs to
   * feed the sales."* One count used to serve both rows, so a message
   * converted in the Front Desk turned a red number on beside Standard Sales
   * & Jobs for work that had nothing to do with it.
   */
  const salesEnquiryBadgeQ = useQuery({
    queryKey: ['sidebar', 'enquiry-badge', 'sales'],
    queryFn: ({ signal }) => fetchEnquiryBadge('sales', signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('sales'),
  });
  /*
   * THE JOBS ROW COUNTS EVERYTHING, exactly as it did before this branch.
   * No section argument, no gate — the query it ran for every tenant every
   * sixty seconds is the query it still runs. Reagan asked for Standard Sales
   * & Jobs to be left alone, and a badge that quietly started counting
   * something narrower would be the kind of change she would find later.
   */
  const standardEnquiryBadgeQ = useQuery({
    queryKey: ['sidebar', 'enquiry-badge'],
    queryFn: ({ signal }) => fetchEnquiryBadge(undefined, signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('standard_sales'),
  });
  const salesEnquiryBadgeCount = salesEnquiryBadgeQ.data ?? 0;
  const standardEnquiryBadgeCount = standardEnquiryBadgeQ.data ?? 0;

  /*
   * WHO IS WAITING TO BE SET UP (2026-10-14). Reagan asked for this three
   * times before it got built: nothing told her a new company was waiting,
   * so the only way to find out was to open the tab and look.
   */
  const onboardingBadgeQ = useQuery({
    queryKey: ['sidebar', 'onboarding-badge'],
    queryFn: ({ signal }) => fetchOnboardingBadge(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('sales'),
  });
  const onboardingBadgeCount = onboardingBadgeQ.data ?? 0;
  const badgeCounts = {
    unassignedSales: salesEnquiryBadgeCount,
    unassignedAnySection: standardEnquiryBadgeCount,
    awaitingOnboarding: onboardingBadgeCount,
  };

  // Session 3.12 — Building Maintenance red overdue statutory inspections badge.
  /*
   * Information Security (2026-10-21). Red, like Fleet: every one of the three
   * things it counts is already late or inside its last day, and the ICO
   * deadline is the one that cannot be made up afterwards.
   */
  const infosecBadgeQ = useQuery({
    queryKey: ['sidebar', 'information-security-badge'],
    queryFn: ({ signal }) => fetchInformationSecurityBadge(signal),
    refetchInterval: 60_000,
    retry: false,
  });
  const infosecBadgeCount = infosecBadgeQ.data ?? 0;

  const buildingMaintenanceBadgeQ = useQuery({
    queryKey: ['sidebar', 'building-maintenance-badge'],
    queryFn: ({ signal }) => fetchBuildingMaintenanceBadge(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('building_maintenance'),
  });
  const buildingMaintenanceBadgeCount = buildingMaintenanceBadgeQ.data ?? 0;

  // Session 3.13 — Accreditations amber count badge (expiring within 30 days).
  const accreditationsBadgeQ = useQuery({
    queryKey: ['sidebar', 'accreditations-badge'],
    queryFn: ({ signal }) => fetchAccreditationsExpiringCount(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('accreditations'),
  });
  const accreditationsBadgeCount = accreditationsBadgeQ.data ?? 0;

  // 2026-06-28 — the Accreditations "Auditor Portal" child only shows when the
  // tenant has an active Achieve subscription (mirrors the page's conditional
  // tab). Same query the page uses.
  const achieveSubsQ = useQuery({
    queryKey: ['sidebar', 'achieve-subs'],
    queryFn: ({ signal }) => fetchAchieveSubscriptions(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('accreditations'),
  });
  const hasActiveAchieveSub = (achieveSubsQ.data ?? []).some(
    (s) => s.status === 'active',
  );

  /*
   * What this tenant is ACCREDITED TO (2026-09-21) — the gate on the FORS and
   * ISO entries below.
   *
   * Until now nothing gated them: both showed for every tenant whatever they
   * held. The only per-tenant answer that existed was
   * `country_sectors.default_standards`, a SECTOR default read live by
   * /applicable-standards, and all it ever decided was which ISO boards appear
   * INSIDE the area — so with every sector carrying an empty list the ISO area
   * showed no boards at all while its nav entry stayed visible.
   *
   * Shane: *"the gating needs to be in this area ... as it will be tennent
   * specific not sector"*. It is now `tenants.active_standards`, ticked per
   * tenant in Platform Admin -> Configuration.
   *
   * No fallback when empty, deliberately: empty means the tenant holds nothing,
   * which is the whole point. The Accreditation Register is NOT gated — it
   * lists the contractor schemes (CHAS, SafeContractor and the rest), which a
   * tenant keeps whether or not it is certified to anything here.
   */
  const activeStandards = tenantQuery.data?.activeStandards ?? [];

  // Session 3.14 — Fleet & Transport red expiring-soon badge.
  const fleetBadgeQ = useQuery({
    queryKey: ['sidebar', 'fleet-badge'],
    queryFn: ({ signal }) => fetchFleetExpiringCount(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('fleet_transport'),
  });
  const fleetBadgeCount = fleetBadgeQ.data ?? 0;

  // Session 3.15 — Plant & Equipment amber overdue-inspection badge.
  const plantBadgeQ = useQuery({
    queryKey: ['sidebar', 'plant-overdue-badge'],
    queryFn: ({ signal }) => fetchPlantOverdueCount(signal),
    refetchInterval: 60_000,
    retry: false,
    enabled: draws('plant_equipment'),
  });
  const plantBadgeCount = plantBadgeQ.data ?? 0;

  /*
   * The Guidance area count came off the sidebar on 21 Sep 2026. Reagan:
   * "can we remove this number."
   *
   * The fetch went with it. It existed ONLY to print that badge, so leaving
   * it would mean every signed-in user fetching the guidance listing on every
   * page load to render nothing - a request whose only remaining purpose is
   * to keep an unused variable company.
   */

  // 2026-06-28 — per-item count badge, usable on BOTH plain links and
  // collapsible parent buttons (so a section keeps its badge after it
  // becomes a dropdown). Returns null when there's no badge / a zero count.
  const renderItemBadge = (labelKey: string): JSX.Element | null => {
    switch (labelKey) {
      case 'approved_suppliers':
        return supplierBadgeCount > 0 ? (
          <CountBadge count={supplierBadgeCount} variant="amber" testId="sidebar-approved-suppliers-badge" />
        ) : null;
      case 'holiday':
        return holidayBadgeCount > 0 ? (
          <CountBadge count={holidayBadgeCount} variant="amber" testId="sidebar-holiday-badge" />
        ) : null;
      /*
       * The unassigned-enquiry badge moved to Standard Sales & Jobs (2026-08-29,
       * SPEC-51 Stage 10). It was keyed to `enquiries_quotations`, so when that
       * section was retired the badge rendered NOWHERE while its query went on
       * firing every sixty seconds. The count already comes from
       * /api/standard-sales/enquiries/badge.
       */
      case 'standard_sales': {
        const n = salesBadgeCount(labelKey, badgeCounts);
        return n === null ? null : (
          <CountBadge count={n} variant="red" testId="sidebar-enquiries-badge" />
        );
      }
      /*
       * Sales carries the same badge (2026-10-13). Reagan: *"i can see the
       * notifications on the standard sales and jobs but not on the sales
       * one"*.
       *
       * NOT the same count any more (2026-10-15) — but not a matched pair
       * either. The SALES row counts enquiries belonging to Sales; the jobs
       * row above still counts every unassigned enquiry, because Reagan asked
       * for Standard Sales & Jobs to be left exactly as it was. So a Front
       * Desk message does still add to the jobs row's number, and that is the
       * decision rather than the bug.
       *
       * Its OWN testId all the same: on a tenant holding both, two elements
       * answering to `sidebar-enquiries-badge` would break any test that
       * asks for it by name, and that tenant is the one we develop on.
       */
      /*
       * THE PARENT ROLLS UP THE ONBOARDING COUNT TOO (2026-10-14), and it has
       * to. A child badge is only visible once the section is open, and the
       * section is only open when you are already in it — so a badge that
       * lived solely on the Onboarding tab would announce a waiting customer
       * to somebody who had already gone looking, which is precisely the
       * problem Reagan reported.
       *
       * A SUM OF TWO DIFFERENT THINGS, deliberately. The parent answers one
       * question — "is there anything in Sales needing me?" — and both do.
       * Which it is, is the child badges’ job, one line further down.
       */
      case 'sales': {
        const n = salesBadgeCount(labelKey, badgeCounts);
        return n === null ? null : (
          <CountBadge count={n} variant="red" testId="sidebar-sales-enquiries-badge" />
        );
      }
      /*
       * ON THE TAB ITSELF. Reagan: a badge on the Onboarding tab. Red rather
       * than amber because this is a customer who has paid and is waiting for
       * their login — not something to look at when there is time.
       */
      case 'sales_onboarding': {
        const n = salesBadgeCount(labelKey, badgeCounts);
        return n === null ? null : (
          <CountBadge count={n} variant="red" testId="sidebar-sales-onboarding-badge" />
        );
      }
      case 'information_security':
        return infosecBadgeCount > 0 ? (
          <CountBadge count={infosecBadgeCount} variant="red" testId="sidebar-information-security-badge" />
        ) : null;
      case 'building_maintenance':
        return buildingMaintenanceBadgeCount > 0 ? (
          <CountBadge count={buildingMaintenanceBadgeCount} variant="blue" testId="sidebar-building-maintenance-badge" />
        ) : null;
      case 'accreditations':
        return accreditationsBadgeCount > 0 ? (
          <CountBadge count={accreditationsBadgeCount} variant="amber" testId="sidebar-accreditations-badge" />
        ) : null;
      case 'fleet_transport':
        return fleetBadgeCount > 0 ? (
          <CountBadge count={fleetBadgeCount} variant="red" testId="sidebar-fleet-badge" />
        ) : null;
      case 'plant_equipment':
        return plantBadgeCount > 0 ? (
          <CountBadge count={plantBadgeCount} variant="amber" testId="sidebar-plant-badge" />
        ) : null;
      default:
        return null;
    }
  };

  /*
   * THE DEVOLVED FILTER HAS TO REACH CHILDREN TOO (2026-09-28).
   *
   * `visibleNavGroups` above filters `group.items` — the TOP level only. That
   * was enough while every devolved module was a section of its own. It stopped
   * being enough the moment pharmacy's banks became nested groups: six clinical
   * modules are devolved (Pharmacy First, its triage, PCS, NMS and Hypertension
   * are England; PIPs and the Welsh DMR are Wales), and as children they would
   * have been offered to every branch in both nations.
   *
   * Nothing would have looked broken. A Welsh pharmacist would simply have had
   * "Pharmacy First" in their sidebar, clicked it, and been refused by the API's
   * own nationRegistry — the check that would otherwise have caught this in
   * production rather than here.
   *
   * It wraps EVERY path out of the function rather than being bolted onto the
   * pharmacy branch, because the rule is general; and it recurses, because the
   * next person to nest a group should not have to remember either fact.
   */
  const nationFiltered = (items: readonly NavItem[]): readonly NavItem[] =>
    navChildrenForNation(items, nation);

  // 2026-06-28 — a parent's effective children, after section-specific
  // conditional tabs:
  //  • Accreditations: append Auditor Portal only with an active Achieve sub.
  //  • Rota: see rotaChildrenFor - two hiding rules plus the testing override.
  const childrenBeforeNation = (item: NavItem): readonly NavItem[] => {
    if (item.labelKey === 'accreditations') {
      // The rule itself is accreditationChildrenFor above - exported and
      // tested. Each standard's entry follows what the tenant is accredited
      // to; the Register and the Auditor Portal do not.
      const held = accreditationChildrenFor(item.children ?? [], activeStandards);
      return hasActiveAchieveSub ? [...held, ACCREDITATIONS_AUDITOR_CHILD] : held;
    }
    if (item.labelKey === 'erp_accounts') {
      // The rule itself is accountsChildrenFor above - exported and tested.
      // "Runs Pharmacy" is the tenant-level question, asked exactly as the
      // Pharmacy entry itself asks it, not whether this person holds it.
      return accountsChildrenFor(item.children ?? [], {
        runsPharmacy: tenantOffersSection('pharmacy', tenantProfile, nation),
        nation,
      });
    }
    if (item.labelKey === 'hr_people') {
      // The rule itself is hrChildrenFor above - exported and tested.
      return hrChildrenFor(item.children ?? [], {
        /*
         * tenantOffersSection, as runsPharmacy two branches above already
         * does. Reading selectedSections through `?.` and `?? []` answers
         * "no pharmacy" while the company is still loading — the same shape
         * Shane's only-your-own-company guard was written to stop, and it
         * caught this one the moment it arrived.
         */
        isPharmacy: tenantOffersSection('pharmacy', tenantProfile, nation),
      });
    }
    if (item.labelKey === 'rota_shifts') {
      /*
       * The rule itself is rotaChildrenFor above - exported and tested.
       *
       * THE SECTION, NOT THE SECTOR (2026-09-30). This read
       * `profileSectors.includes('pharmacy')`, and it has to match what the
       * API enforces or the sidebar hands out a link that can only 403:
       * /api/pharmacy-rota is mounted `section(..., 'pharmacy', ...)`. A
       * tenant whose SECTOR is pharmacy but who does not hold the section
       * would have been sent to a page its own API refuses; a tenant holding
       * the section with some other sector - gb-test-tenant is exactly that,
       * sector 'manufacturing' - was sent to the generic builder instead of
       * theirs, which is also why this was invisible in development.
       */
      return rotaChildrenFor(item.children ?? [], {
        isPharmacy:
          tenantProfile !== undefined &&
          tenantHasSection('pharmacy', tenantProfile.selectedSections ?? [], tenantProfile.hiddenSections ?? []),
        shiftManagementEnabled,
        overrideAllShiftPatterns,
      });
    }
    // Fleet: Plus-only tabs (Journey Log, Tachograph, Site Manager, Transport
    // Manager Admin, Fuel, EU 561) appear only for Plus/Customised tenants;
    // Fleet Admin stays on both tiers. My Credentials is TM-only.
    if (item.labelKey === 'fleet_transport') {
      const PLUS_ONLY = new Set([
        '/fleet?tab=journeys',
        '/fleet?tab=tacho',
        '/fleet?tab=sitemanager',
        '/fleet?tab=tmadmin',
        '/fleet?tab=fuel',
        '/fleet?tab=eu561',
      ]);
      return (item.children ?? []).filter((c) => {
        if (c.to === '/fleet?tab=tmcredentials') return isTransportManager;
        if (PLUS_ONLY.has(c.to)) return fleetIsPlus;
        return true;
      });
    }
    /*
     * THE ENQUIRIES AND JOBS TIER FILTERS WERE REMOVED HERE on 2026-08-29
     * (SPEC-51 Stage 10), along with the two nav items they keyed on.
     *
     * They decided which children showed at which tier. With no
     * `enquiries_quotations` or `jobs_projects` item left in NAV_GROUPS the
     * branches could never be entered, and the two `useSectionTiers` lookups
     * feeding them asked the server about sections that no longer exist.
     *
     * Standard Sales & Jobs shows all seven children on every tier - it is the
     * plain offering, not a tiered one. The switchboard's branch is untouched:
     * its leaves hang off `switchboard_builder`, never these.
     */
    // Stock: Usage and Barcode are the barcode-scanning add-on (the
    // Customised level); everything else, PO Tracker included, is Base.
    if (item.labelKey === 'stock_management') {
      const CUSTOMISED = new Set(['/stock?tab=usage', '/stock?tab=barcode']);
      return (item.children ?? []).filter((c) => !CUSTOMISED.has(c.to) || stockTier === 'customised');
    }
    // Pharmacy: each clinical module is hidden unless the tenant has that
    // module switched on (CS-02 HM-01). The API refuses them either way; this
    // stops a shortcut leading to a dead page.
    if (item.labelKey === 'pharmacy') {
      return (item.children ?? [])
        /*
         * CARE HOME IS ONE ROW OVER NINE SCREENS, AND THEY ARE NOT ALL ONE
         * MODULE (2026-10-01, where the two branches met).
         *
         * The row points at `/care-home-receive` because a nested parent has
         * to name its own first leaf, and it covers all nine through
         * `activeWhenIn`. But five of those nine are the care_homes module,
         * two are blister_packs, and two (contracts, dossette admin) are
         * always live. Judging the row by its single `to` therefore got both
         * directions wrong: with care_homes off the row vanished, taking the
         * two live screens and any blister-pack ones with it; with
         * care_homes on it stayed, offering blister screens the tenant has
         * not been signed off for.
         *
         * So the row is kept when ANY of its leaves is offered, and sent to
         * the first leaf that actually is. The strip inside then shows that
         * tenant's subset — see PharmacyModuleStrips.
         */
        .flatMap((c) => {
          // By labelKey, like the stock rule above — not by comparing
          // `activeWhenIn` against the imported array, which would hold only
          // while the nav item happens to pass that same reference.
          // A bank row opens on its first module this branch is offered.
          const bank = CATEGORIES.find((b) => `pharmacy_bank_${b.id}` === c.labelKey);
          if (bank) {
            const first = (bank.tiles ?? []).find(
              (m) => moduleVisibleInNation(m.labelKey, nation) && pageOfferedTo(m.to, tenantProfile?.clinicalModules),
            );
            return first ? [{ ...c, to: first.to }] : [];
          }
          if (c.labelKey !== 'care_home') return [c];
          const open = CARE_HOME_PATHS.filter((p) => pageOfferedTo(p, tenantProfile?.clinicalModules));
          return open.length > 0 ? [{ ...c, to: open[0] as string, activeWhenIn: open }] : [];
        })
        .filter((c) => pageOfferedTo(c.to, tenantProfile?.clinicalModules));
    }
    return item.children ?? [];
  };

  /** Section rules first, then the devolved filter over whatever they returned, then a child's own rules. */
  const effectiveChildren = (item: NavItem): readonly NavItem[] =>
    nationFiltered(childrenBeforeNation(item)).filter((c) =>
      childShown(c, { role: currentRole, isOperator: tenantProfile?.isOperator === true }),
    );

  /*
   * What this person can open is what this sidebar drew: published for the page
   * guard and the links (useCanOpen), so they ask this rule and no copy of it.
   */
  const navReady = Boolean(tenantQuery.data && meQuery.data);
  const drawnMenu: NavMenu | null = navReady
    ? {
        groups: visibleNavGroups.map((group) => ({
          ...group,
          items: group.items.map((item) => (item.children ? { ...item, children: [...effectiveChildren(item)] } : item)),
        })),
        guidance: GUIDANCE_CHILDREN,
        howTo: HOW_TO_CHILDREN,
        childrenOf: (item) => item.children,
      }
    : null;
  // Republished only when what was drawn changes: every address, and what each entry covers.
  const drawnParts: string[] = [];
  const addParts = (items: readonly NavItem[]): void => {
    for (const i of items) {
      drawnParts.push(`${i.labelKey}=${i.to}|${(i.activeWhenIn ?? []).join(',')}`);
      if (i.children) addParts(i.children);
    }
  };
  if (drawnMenu) {
    for (const group of drawnMenu.groups) addParts(group.items);
    addParts(drawnMenu.guidance);
    addParts(drawnMenu.howTo);
  }
  const drawnKey = drawnMenu ? drawnParts.join('\n') : null;
  const drawnMenuRef = useRef<NavMenu | null>(null);
  drawnMenuRef.current = drawnMenu;
  useEffect(() => {
    publishDrawnNav(drawnMenuRef.current);
  }, [drawnKey]);
  useEffect(() => () => publishDrawnNav(null), []);

  // The wording is fetched: until it arrives, t() prints its keys.
  if (i18n?.exists && !i18n.exists('sidebar.your_business')) {
    return <nav className="sb" aria-hidden="true" data-testid="sidebar-waiting" />;
  }

  return (
    /*
     * TUTORIAL-STATE-001 anchors (2026-09-21). Every nav entry carries one,
     * derived from its labelKey, so the tour can name any of them without this
     * file knowing which it uses. The tour skips an anchor that is not on the
     * page, which is how one step list survives four layers of filtering
     * (role allow-list, hiddenSections, operating sector, feature flag).
     */
    <nav className="sb" aria-label={t('sidebar.your_business')} data-tutorial="sidebar">
      {/* JeanieIQ logo — wide horizontal artwork (lamp + lightbulb + wordmark
          + "Smart Business. Full Compliance." tagline). PNG lives in
          apps/web/public/jeanie-iq-white-writing.png — Vite serves it at the site
          root. If the file is missing the sidebar shows a broken-image
          icon; the text rebrand still works regardless. */}
      <div className="sb-logo">
        <img
          src="/jeanie-iq-white-writing.png"
          alt="JeanieIQ — Smart Business. Full Compliance."
          style={{ width: '100%', maxWidth: 200, height: 'auto', display: 'block' }}
        />
      </div>

      {/* The company's own name and sector, or nothing: never a stand-in, which
          reads as somebody else's company. */}
      <div className="sb-tenant">
        <div className="sb-co">{t('sidebar.your_business')}</div>
        <div className="sb-coname" data-testid="sidebar-tenant-name">
          {tenantQuery.data?.name ?? ' '}
        </div>
        {tenantQuery.data && (
          <div className="sb-badge" data-testid="sidebar-tenant-badge">
            {tenantPlace(tenantQuery.data)}
          </div>
        )}
        {meQuery.data?.user && (
          <div
            data-testid="sidebar-signed-in"
            style={{ fontSize: 11, color: 'var(--white)', opacity: 0.85, marginTop: 6 }}
          >
            {t('sidebar.signed_in', { name: `${meQuery.data.user.firstName} ${meQuery.data.user.lastName}`.trim() })}
          </div>
        )}
      </div>

      {/* Guidance Engine — 2026-06-28 sidebar-dropdowns update: now a
          collapsible parent (Reference Library / Contextual Panel / Wizard
          drop down beneath it) instead of a single link into the page's
          in-page tab bar. Always the first sidebar item, above Admin. */}
      <div>
        <button
          type="button"
          className={`ni-guide${sectionExpanded(GUIDANCE_CHILDREN) ? ' active' : ''}`}
          aria-expanded={sectionExpanded(GUIDANCE_CHILDREN)}
          onClick={() => toggleSection(GUIDANCE_CHILDREN)}
          data-testid="sidebar-guidance-engine"
          data-tutorial="guidance-engine"
          // `font: inherit` was here. It is a SHORTHAND, so it reset family, size AND
          // weight to whatever the sidebar inherits from body - overriding .ni, and
          // inline beats class. That is why a collapsible parent (a <button>) rendered
          // visibly bigger than a standalone section (an <a>). .ni already gives the
          // button 12.5px / var(--ff) / 500, which is the whole point of the class.
          //
          // 2026-08-24 — `width: '100%'` removed for the same reason, one line
          // further on. .ni-guide sets `width: calc(100% - 8px)` precisely to
          // leave room for its own `margin: 6px 4px`; the inline 100% overrode
          // that, making the button 8px WIDER than the 228px sidebar. That is
          // why "Guidance Engine" was cut off — it was not a font or a label
          // length, it was this. textAlign is on the class too, so it went with
          // it; cursor stays because .ni-guide's own cursor rule is the one
          // thing here the class does not repeat for the button element.
          style={{ cursor: 'pointer' }}
        >
          {/*
            GUIDANCE-SIDEBAR-B2-001 — the set icon, not an emoji. The three
            children below already lost theirs; leaving one on the parent
            made it the only emoji in a 265-item sidebar.
          */}
          <div className="ni-ico" aria-hidden="true">
            {Icon.glossary}
          </div>
          <span className="ni-label">{t('sidebar.guidance_engine')}</span>
          <span className="ni-chev" aria-hidden="true">
            {sectionExpanded(GUIDANCE_CHILDREN) ? '▾' : '▸'}
          </span>
        </button>
        {sectionExpanded(GUIDANCE_CHILDREN) && renderTabChildren(GUIDANCE_CHILDREN)}
      </div>

      {/*
        HOW-TO — 21 Sep 2026. Videos on driving the platform, for everybody
        signed in.

        PINNED HERE, not under Administration, which is where the idea
        started. Every /admin route is gated to director, manager and admin,
        so it is the one place a library every user needs cannot live.

        AND NOT CALLED GUIDANCE OR TRAINING. Both names are taken and both
        mean something else: the Guidance Engine above is what the LAW
        requires of the business, and Training Records further down is HR
        training compliance — certificates and expiry. Somebody clicking
        "Training" for their forklift ticket and getting a video about
        invoicing is a bad moment.
      */}
      <div>
        <button
          type="button"
          className={`ni-guide${sectionExpanded(HOW_TO_CHILDREN) ? ' active' : ''}`}
          aria-expanded={sectionExpanded(HOW_TO_CHILDREN)}
          onClick={() => toggleSection(HOW_TO_CHILDREN)}
          data-testid="sidebar-how-to"
          /*
           * TUTORIAL-STATE-001 anchor (2026-09-24). On the collapsible PARENT,
           * not on either of its two children — a child only exists while the
           * block is expanded, and an anchor that lives inside something
           * collapsed is the exact fault that made the Assist step skip for
           * everybody without a word.
           */
          data-tutorial="how-to"
          style={{ cursor: 'pointer' }}
        >
          <div className="ni-ico" aria-hidden="true">
            {Icon.training}
          </div>
          <span className="ni-label">{t('sidebar.how_to')}</span>
          <span className="ni-chev" aria-hidden="true">
            {sectionExpanded(HOW_TO_CHILDREN) ? '▾' : '▸'}
          </span>
        </button>
        {sectionExpanded(HOW_TO_CHILDREN) && renderTabChildren(HOW_TO_CHILDREN)}
      </div>

      {/* Grouped nav items — filtered per tenant.hiddenSections */}
      {visibleNavGroups.map((group) => (
        <div key={group.titleKey ?? '__top'}>
          {group.titleKey && (
            <div className="sb-sec">{t(`sidebar.groups.${group.titleKey}`)}</div>
          )}
          {group.items.map((item) =>
            item.children ? (
              <div key={item.labelKey}>
                <button
                  type="button"
                  className={`ni${sectionExpanded(effectiveChildren(item)) ? ' active' : ''}`}
                  aria-expanded={sectionExpanded(effectiveChildren(item))}
                  data-tutorial={`nav-${item.labelKey}`}
                  onClick={() => toggleSection(effectiveChildren(item))}
                  style={{ width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer' }}
                >
                  <div className="ni-ico">{item.icon}</div>
                  <span className="ni-label">{t(`sidebar.nav.${item.labelKey}`)}</span>
                  {renderItemBadge(item.labelKey)}
                  {/*
                    SIDEBAR-BADGE-ALIGN-001 — see .ni-chev. This carried its
                    own marginLeft:auto, which is why parent badges did not
                    line up with leaf ones: two auto margins in a flex row
                    SPLIT the free space, so the badge floated into the
                    middle of it instead of sitting against the arrow.
                  */}
                  <span className="ni-chev" aria-hidden="true">
                    {sectionExpanded(effectiveChildren(item)) ? '▾' : '▸'}
                  </span>
                </button>
                {sectionExpanded(effectiveChildren(item)) &&
                  renderTabChildren(effectiveChildren(item))}
              </div>
            ) : (
            <NavLink
              key={item.to}
              to={item.to}
              data-tutorial={`nav-${item.labelKey}`}
              /*
               * 2026-08-20 (ASSETS-MAINTENANCE-001) — a standalone SECTION can
               * now be a `?tab=` route: Fleet Equipment and Calibration are
               * their own sections but both live on /plant, alongside Plant &
               * Equipment. NavLink's isActive is path-only, so all three lit up
               * together — Shane: "when i clicked on one thing 3 were
               * highlighting as was still linked to the original section".
               *
               * Same fix the tab CHILDREN already use: when the item's `to`
               * carries a ?tab=, match on path AND tab and ignore isActive.
               */
              className={({ isActive }) => {
                const [itemPath, itemQuery] = item.to.split('?');
                const current = new URLSearchParams(search);
                let matched: boolean;
                if (itemQuery) {
                  // A section that is one view of a shared page: /plant?tab=…
                  // and /maintenance-schedule?type=vehicle. Every param the
                  // item declares must match, or the siblings on that path all
                  // light up together.
                  matched = pathname === itemPath;
                  if (matched) {
                    for (const [k, v] of new URLSearchParams(itemQuery)) {
                      if (current.get(k) !== v) {
                        matched = false;
                        break;
                      }
                    }
                  }
                } else if (
                  pathname === itemPath &&
                  (current.get('tab') || current.get('type'))
                ) {
                  // The unfiltered view of a page whose other sections ARE
                  // filtered — active only when no discriminator is set, so
                  // "Maintenance Schedule" does not stay lit on "Vehicles".
                  matched = false;
                } else {
                  /*
                   * SIDEBAR-ACTIVE-PREFIX-001 (2026-09-17). Reagan: "when
                   * clicking on document register the admin panel lights up
                   * at the same time".
                   *
                   * The Admin hub declares activePrefix "/admin" so it stays
                   * lit on the sub-pages reached from its cards. Its own
                   * docstring lists those as Subscription, Users, Document
                   * Register and Kiosks — "sub-pages the nav does not
                   * model". Document Register got its own sidebar row later,
                   * so the prefix was claiming a page that has a row of its
                   * own, and both lit.
                   *
                   * A prefix is a fallback for paths nothing else owns. It
                   * now yields to any entry pointing exactly here, which is
                   * the same rule navEntryActive already applies to
                   * activeTabs ("claimedByAnother"). Nothing has to be
                   * maintained when a row is added: the prefix simply stops
                   * claiming that path.
                   */
                  const ownedByAnother =
                    Boolean(item.activePrefix) &&
                    allNavEntries().some(
                      (other) =>
                        other !== item && navChildPath(other.to) === pathname,
                    );
                  matched =
                    isActive ||
                    (!ownedByAnother &&
                      matchesActivePrefix(item.activePrefix, pathname));
                }
                return `ni${matched ? ' active' : ''}`;
              }}
            >
              <div className="ni-ico">{item.icon}</div>
              <span className="ni-label">{t(`sidebar.nav.${item.labelKey}`)}</span>
              {item.badge && (
                <span className={item.badge.variant === 'gold' ? 'nb-g' : 'nb'}>
                  {t(`sidebar.${item.badge.text}`)}
                </span>
              )}
              {renderItemBadge(item.labelKey)}
              {/*
                SIDEBAR-BADGE-ALIGN-001 — an empty slot the width of the
                arrow. Reagan: "make sure all notifications on the side bar
                line up at the end of the header before the arrow".

                Without it the badges still do not line up between rows,
                because a leaf row has no arrow: its badge would run a
                chevron-width further right than the badge on the group
                above it. Reserving the space on every row is what makes
                the column straight.
              */}
              <span className="ni-chev" aria-hidden="true" />
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}
